import {
  useNotificationsStore,
  useTheme,
  useTranslation
} from "./chunk-AFNJZUOE.js";
import "./chunk-53GZFQK3.js";
import {
  cn
} from "./chunk-IX6RFIQL.js";

// src/components/Accordion/AccordionContent.tsx
import { forwardRef } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { jsx } from "react/jsx-runtime";
var AccordionContent = forwardRef(function AccordionContent2({ children, className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    AccordionPrimitive.Content,
    {
      className: "overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
      ref,
      ...props,
      children: /* @__PURE__ */ jsx("div", { className: cn("pb-4 pt-0", className), children })
    }
  );
});

// src/components/Accordion/AccordionItem.tsx
import { forwardRef as forwardRef2 } from "react";
import * as AccordionPrimitive2 from "@radix-ui/react-accordion";
import { jsx as jsx2 } from "react/jsx-runtime";
var AccordionItem = forwardRef2(function AccordionItem2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx2(AccordionPrimitive2.Item, { className: cn("border-b", className), ref, ...props });
});

// src/components/Accordion/AccordionRoot.tsx
import { forwardRef as forwardRef3 } from "react";
import * as AccordionPrimitive3 from "@radix-ui/react-accordion";
import { jsx as jsx3 } from "react/jsx-runtime";
var AccordionRoot = forwardRef3(function AccordionRoot2(props, ref) {
  return /* @__PURE__ */ jsx3(AccordionPrimitive3.Root, { "data-testid": "accordion", ref, ...props });
});

// src/components/Accordion/AccordionTrigger.tsx
import { forwardRef as forwardRef4 } from "react";
import * as AccordionPrimitive4 from "@radix-ui/react-accordion";
import { ChevronDownIcon } from "lucide-react";
import { jsx as jsx4, jsxs } from "react/jsx-runtime";
var AccordionTrigger = forwardRef4(function AccordionTrigger2({ children, className, ...props }, ref) {
  return /* @__PURE__ */ jsx4(AccordionPrimitive4.Header, { className: "flex", children: /* @__PURE__ */ jsxs(
    AccordionPrimitive4.Trigger,
    {
      className: cn(
        "flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
        className
      ),
      ref,
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx4(ChevronDownIcon, { className: "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" })
      ]
    }
  ) });
});

// src/components/Accordion/Accordion.tsx
var Accordion = Object.assign(AccordionRoot, {
  Content: AccordionContent,
  Item: AccordionItem,
  Trigger: AccordionTrigger
});

// src/components/DropdownButton/DropdownButton.tsx
import { forwardRef as forwardRef5 } from "react";
import { ChevronDownIcon as ChevronDownIcon2 } from "lucide-react";
import { jsx as jsx5, jsxs as jsxs2 } from "react/jsx-runtime";
var DropdownButton = forwardRef5(
  function DropdownButton2({ children, className, ...props }, ref) {
    return /* @__PURE__ */ jsxs2(
      "button",
      {
        className: cn(
          "flex h-9 w-full items-center gap-2 whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
          children ? "justify-between" : "justify-end",
          className
        ),
        ref,
        type: "button",
        ...props,
        children: [
          children,
          /* @__PURE__ */ jsx5(ChevronDownIcon2, { className: "h-4 w-4 opacity-50" })
        ]
      }
    );
  }
);

// src/components/DropdownMenu/DropdownMenu.tsx
import * as DropdownMenuPrimitive9 from "@radix-ui/react-dropdown-menu";

// src/components/DropdownMenu/DropdownMenuCheckboxItem.tsx
import { forwardRef as forwardRef6 } from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { CheckIcon } from "lucide-react";
import { jsx as jsx6, jsxs as jsxs3 } from "react/jsx-runtime";
var DropdownMenuCheckboxItem = forwardRef6(function DropdownMenuCheckboxItem2({ checked, children, className, ...props }, ref) {
  return /* @__PURE__ */ jsxs3(
    DropdownMenuPrimitive.CheckboxItem,
    {
      checked,
      className: cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      ),
      ref,
      ...props,
      children: [
        /* @__PURE__ */ jsx6("span", { className: "absolute right-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx6(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx6(CheckIcon, { className: "h-4 w-4" }) }) }),
        children
      ]
    }
  );
});

// src/components/DropdownMenu/DropdownMenuContent.tsx
import * as React from "react";
import * as DropdownMenuPrimitive2 from "@radix-ui/react-dropdown-menu";
import { jsx as jsx7 } from "react/jsx-runtime";
var DropdownMenuContent = React.forwardRef(function DropdownMenuContent2({ className, sideOffset = 4, widthFull = false, ...props }, ref) {
  return /* @__PURE__ */ jsx7(DropdownMenuPrimitive2.Portal, { children: /* @__PURE__ */ jsx7(
    DropdownMenuPrimitive2.Content,
    {
      className: cn(
        "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        widthFull && "w-[var(--radix-dropdown-menu-trigger-width)]",
        className
      ),
      ref,
      sideOffset,
      ...props
    }
  ) });
});

// src/components/DropdownMenu/DropdownMenuItem.tsx
import { forwardRef as forwardRef8 } from "react";
import * as DropdownMenuPrimitive3 from "@radix-ui/react-dropdown-menu";
import { jsx as jsx8 } from "react/jsx-runtime";
var DropdownMenuItem = forwardRef8(function DropdownMenuItem2({ className, inset, ...props }, ref) {
  return /* @__PURE__ */ jsx8(
    DropdownMenuPrimitive3.Item,
    {
      className: cn(
        "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        inset && "pl-8",
        className
      ),
      ref,
      ...props
    }
  );
});

// src/components/DropdownMenu/DropdownMenuLabel.tsx
import { forwardRef as forwardRef9 } from "react";
import * as DropdownMenuPrimitive4 from "@radix-ui/react-dropdown-menu";
import { jsx as jsx9 } from "react/jsx-runtime";
var DropdownMenuLabel = forwardRef9(function DropdownMenuLabel2({ className, inset, ...props }, ref) {
  return /* @__PURE__ */ jsx9(
    DropdownMenuPrimitive4.Label,
    {
      className: cn("px-2 py-1.5 text-sm font-semibold", inset && "pl-8", className),
      ref,
      ...props
    }
  );
});

// src/components/DropdownMenu/DropdownMenuRadioItem.tsx
import { forwardRef as forwardRef10 } from "react";
import * as DropdownMenuPrimitive5 from "@radix-ui/react-dropdown-menu";
import { CircleIcon } from "lucide-react";
import { jsx as jsx10, jsxs as jsxs4 } from "react/jsx-runtime";
var DropdownMenuRadioItem = forwardRef10(function DropdownMenuRadioItem2({ children, className, ...props }, ref) {
  return /* @__PURE__ */ jsxs4(
    DropdownMenuPrimitive5.RadioItem,
    {
      className: cn(
        "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      ),
      ref,
      ...props,
      children: [
        /* @__PURE__ */ jsx10("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx10(DropdownMenuPrimitive5.ItemIndicator, { children: /* @__PURE__ */ jsx10(CircleIcon, { className: "h-4 w-4 fill-current" }) }) }),
        children
      ]
    }
  );
});

// src/components/DropdownMenu/DropdownMenuSeparator.tsx
import { forwardRef as forwardRef11 } from "react";
import * as DropdownMenuPrimitive6 from "@radix-ui/react-dropdown-menu";
import { jsx as jsx11 } from "react/jsx-runtime";
var DropdownMenuSeparator = forwardRef11(function DropdownMenuSeparator2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx11(DropdownMenuPrimitive6.Separator, { className: cn("-mx-1 my-1 h-px bg-muted", className), ref, ...props });
});

// src/components/DropdownMenu/DropdownMenuShortcut.tsx
import "react";
import { jsx as jsx12 } from "react/jsx-runtime";
var DropdownMenuShortcut = ({ className, ...props }) => {
  return /* @__PURE__ */ jsx12("span", { className: cn("ml-auto text-xs tracking-widest opacity-60", className), ...props });
};

// src/components/DropdownMenu/DropdownMenuSubContent.tsx
import { forwardRef as forwardRef12 } from "react";
import * as DropdownMenuPrimitive7 from "@radix-ui/react-dropdown-menu";
import { jsx as jsx13 } from "react/jsx-runtime";
var DropdownMenuSubContent = forwardRef12(function DropdownMenuSubContent2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx13(
    DropdownMenuPrimitive7.SubContent,
    {
      className: cn(
        "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      ),
      ref,
      ...props
    }
  );
});

// src/components/DropdownMenu/DropdownMenuSubTrigger.tsx
import { forwardRef as forwardRef13 } from "react";
import * as DropdownMenuPrimitive8 from "@radix-ui/react-dropdown-menu";
import { ChevronRightIcon } from "lucide-react";
import { jsx as jsx14, jsxs as jsxs5 } from "react/jsx-runtime";
var DropdownMenuSubTrigger = forwardRef13(function DropdownMenuSubTrigger2({ children, className, inset, ...props }, ref) {
  return /* @__PURE__ */ jsxs5(
    DropdownMenuPrimitive8.SubTrigger,
    {
      className: cn(
        "flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent",
        inset && "pl-8",
        className
      ),
      ref,
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx14(ChevronRightIcon, { className: "ml-auto h-4 w-4" })
      ]
    }
  );
});

// src/components/DropdownMenu/DropdownMenu.tsx
var DropdownMenu = Object.assign(DropdownMenuPrimitive9.Root.bind(null), {
  CheckboxItem: DropdownMenuCheckboxItem,
  Content: DropdownMenuContent,
  Group: DropdownMenuPrimitive9.Group,
  Item: DropdownMenuItem,
  Label: DropdownMenuLabel,
  Portal: DropdownMenuPrimitive9.Portal,
  RadioGroup: DropdownMenuPrimitive9.RadioGroup,
  RadioItem: DropdownMenuRadioItem,
  Separator: DropdownMenuSeparator,
  Shortcut: DropdownMenuShortcut,
  Sub: DropdownMenuPrimitive9.Sub,
  SubContent: DropdownMenuSubContent,
  SubTrigger: DropdownMenuSubTrigger,
  Trigger: DropdownMenuPrimitive9.Trigger
});

// src/components/ActionDropdown/ActionDropdown.tsx
import { jsx as jsx15, jsxs as jsxs6 } from "react/jsx-runtime";
function ActionDropdown({
  align = "start",
  className,
  contentClassName,
  disabled,
  onSelection,
  options,
  title,
  triggerClassName,
  widthFull,
  ...props
}) {
  const optionKeys = options instanceof Array ? options : Object.keys(options);
  return /* @__PURE__ */ jsx15(DropdownMenu, { children: /* @__PURE__ */ jsxs6("div", { className: cn("w-full", className), ...props, children: [
    /* @__PURE__ */ jsx15(DropdownMenu.Trigger, { asChild: true, disabled, children: /* @__PURE__ */ jsx15(DropdownButton, { className: triggerClassName, children: title }) }),
    /* @__PURE__ */ jsx15(DropdownMenu.Content, { align, className: contentClassName, widthFull, children: /* @__PURE__ */ jsx15(DropdownMenu.Group, { children: optionKeys.map((option) => /* @__PURE__ */ jsx15(
      DropdownMenu.Item,
      {
        onClick: () => {
          onSelection(option);
        },
        children: Array.isArray(options) ? option : options[option]
      },
      option
    )) }) })
  ] }) });
}

// src/components/AlertDialog/AlertDialog.tsx
import { Root as Root3, Trigger as Trigger3 } from "@radix-ui/react-alert-dialog";

// src/components/AlertDialog/AlertDialogAction.tsx
import { forwardRef as forwardRef15 } from "react";
import { Action } from "@radix-ui/react-alert-dialog";

// src/components/Button/Button.tsx
import * as React3 from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { jsx as jsx16, jsxs as jsxs7 } from "react/jsx-runtime";
var BUTTON_ICON_SIZE = {
  lg: 18,
  md: 16,
  sm: 14
};
var buttonVariants = cva(
  "flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    defaultVariants: {
      size: "md",
      variant: "primary"
    },
    variants: {
      size: {
        icon: "p-1.5 aspect-square",
        lg: "h-10 rounded-md px-8",
        md: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs"
      },
      variant: {
        danger: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        outline: "border border-input bg-inherit shadow-sm",
        primary: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        secondary: "bg-secondary border text-secondary-foreground shadow-sm hover:bg-secondary/80"
      }
    }
  }
);
var Button = React3.forwardRef(function Button2({ asChild, children, className, label, size = "md", variant = "primary", ...props }, ref) {
  const Comp = asChild ? Slot : "button";
  return /* @__PURE__ */ jsxs7(Comp, { className: cn(buttonVariants({ className, size, variant })), ref, ...props, children: [
    label,
    /* @__PURE__ */ jsx16(Slottable, { children })
  ] });
});

// src/components/AlertDialog/AlertDialogAction.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
var AlertDialogAction = forwardRef15(function AlertDialogAction2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx17(Action, { className: cn(buttonVariants(), className), ref, ...props });
});

// src/components/AlertDialog/AlertDialogCancel.tsx
import { forwardRef as forwardRef16 } from "react";
import { Cancel } from "@radix-ui/react-alert-dialog";
import { jsx as jsx18 } from "react/jsx-runtime";
var AlertDialogCancel = forwardRef16(function AlertDialogCancel2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx18(Cancel, { className: cn(buttonVariants({ variant: "outline" }), "mt-2 sm:mt-0", className), ref, ...props });
});

// src/components/AlertDialog/AlertDialogContent.tsx
import { forwardRef as forwardRef18 } from "react";
import { Content as Content3, Portal as Portal3 } from "@radix-ui/react-alert-dialog";

// src/components/AlertDialog/AlertDialogOverlay.tsx
import { forwardRef as forwardRef17 } from "react";
import { Overlay } from "@radix-ui/react-alert-dialog";
import { jsx as jsx19 } from "react/jsx-runtime";
var AlertDialogOverlay = forwardRef17(function AlertDialogOverlay2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx19(
    Overlay,
    {
      className: cn(
        "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      ),
      ...props,
      ref
    }
  );
});

// src/components/AlertDialog/AlertDialogContent.tsx
import { jsx as jsx20, jsxs as jsxs8 } from "react/jsx-runtime";
var AlertDialogContent = forwardRef18(function AlertDialogContent2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsxs8(Portal3, { children: [
    /* @__PURE__ */ jsx20(AlertDialogOverlay, {}),
    /* @__PURE__ */ jsx20(
      Content3,
      {
        className: cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        ),
        ref,
        ...props
      }
    )
  ] });
});

// src/components/AlertDialog/AlertDialogDescription.tsx
import { forwardRef as forwardRef19 } from "react";
import { Description } from "@radix-ui/react-alert-dialog";
import { jsx as jsx21 } from "react/jsx-runtime";
var AlertDialogDescription = forwardRef19(function AlertDialogDescription2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx21(Description, { className: cn("text-sm text-muted-foreground", className), ref, ...props });
});

// src/components/AlertDialog/AlertDialogFooter.tsx
import "react";
import { jsx as jsx22 } from "react/jsx-runtime";
var AlertDialogFooter = ({ className, ...props }) => /* @__PURE__ */ jsx22("div", { className: cn("flex flex-col gap-x-2 gap-y-0.5 sm:flex-row", className), ...props });

// src/components/AlertDialog/AlertDialogHeader.tsx
import "react";
import { jsx as jsx23 } from "react/jsx-runtime";
var AlertDialogHeader = ({ className, ...props }) => /* @__PURE__ */ jsx23("div", { className: cn("flex flex-col space-y-2 text-center sm:text-left", className), ...props });

// src/components/AlertDialog/AlertDialogTitle.tsx
import { forwardRef as forwardRef20 } from "react";
import { Title } from "@radix-ui/react-alert-dialog";
import { jsx as jsx24 } from "react/jsx-runtime";
var AlertDialogTitle = forwardRef20(function AlertDialogTitle2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx24(Title, { className: cn("text-lg font-semibold", className), ref, ...props });
});

// src/components/AlertDialog/AlertDialog.tsx
var AlertDialog = Object.assign(Root3.bind(null), {
  Action: AlertDialogAction,
  Cancel: AlertDialogCancel,
  Content: AlertDialogContent,
  Description: AlertDialogDescription,
  Footer: AlertDialogFooter,
  Header: AlertDialogHeader,
  Overlay: AlertDialogOverlay,
  Title: AlertDialogTitle,
  Trigger: Trigger3
});

// src/components/ArrowToggle/ArrowToggle.tsx
import { useMemo } from "react";
import * as React6 from "react";
import { ChevronUpIcon } from "lucide-react";
import { jsx as jsx25, jsxs as jsxs9 } from "react/jsx-runtime";
var ArrowToggle = React6.forwardRef(function ArrowToggle2({ children, className, isToggled, position, rotation = 0, size = "icon", variant = "ghost", ...props }, ref) {
  const computedRotation = useMemo(() => {
    const toggleRotation = isToggled ? rotation : 0;
    switch (position) {
      case "down":
        return 180 + toggleRotation;
      case "left":
        return 270 + toggleRotation;
      case "right":
        return 90 + toggleRotation;
      case "up":
        return 0 + toggleRotation;
    }
  }, [position, rotation, isToggled]);
  return /* @__PURE__ */ jsxs9(
    Button,
    {
      className: cn(size === "icon" && "h-6 w-6", className),
      "data-testid": "arrow-toggle",
      ref,
      size,
      type: "button",
      variant,
      ...props,
      children: [
        /* @__PURE__ */ jsx25(
          ChevronUpIcon,
          {
            className: "transform-gpu transition-transform",
            "data-testid": "arrow-up-icon",
            style: { transform: `rotate(${computedRotation}deg)` }
          }
        ),
        children
      ]
    }
  );
});

// src/components/Avatar/AvatarFallback.tsx
import { forwardRef as forwardRef22 } from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { jsx as jsx26 } from "react/jsx-runtime";
var AvatarFallback = forwardRef22(function AvatarFallback2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx26(
    AvatarPrimitive.Fallback,
    {
      className: cn("flex h-full w-full items-center justify-center rounded-full bg-muted", className),
      ref,
      ...props
    }
  );
});

// src/components/Avatar/AvatarImage.tsx
import { forwardRef as forwardRef23 } from "react";
import * as AvatarPrimitive2 from "@radix-ui/react-avatar";
import { jsx as jsx27 } from "react/jsx-runtime";
var AvatarImage = forwardRef23(function AvatarImage2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx27(AvatarPrimitive2.Image, { className: cn("aspect-square h-full w-full", className), ref, ...props });
});

// src/components/Avatar/AvatarRoot.tsx
import { forwardRef as forwardRef24 } from "react";
import * as AvatarPrimitive3 from "@radix-ui/react-avatar";
import { jsx as jsx28 } from "react/jsx-runtime";
var AvatarRoot = forwardRef24(function AvatarRoot2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx28(
    AvatarPrimitive3.Root,
    {
      className: cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className),
      "data-testid": "avatar",
      ref,
      ...props
    }
  );
});

// src/components/Avatar/Avatar.tsx
var Avatar = Object.assign(AvatarRoot, {
  Fallback: AvatarFallback,
  Image: AvatarImage
});

// src/components/Badge/Badge.tsx
import "react";
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx29 } from "react/jsx-runtime";
var badgeVariants = cva2(
  "inline-flex items-center rounded-sm border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    defaultVariants: {
      variant: "default"
    },
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "text-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80"
      }
    }
  }
);
var Badge = ({ className, variant, ...props }) => {
  return /* @__PURE__ */ jsx29("div", { className: cn(badgeVariants({ variant }), className), "data-testid": "badge", ...props });
};

// src/components/Breadcrumb/BreadcrumbEllipsis.tsx
import "react";
import { MoreHorizontalIcon } from "lucide-react";
import { jsx as jsx30 } from "react/jsx-runtime";
var BreadcrumbEllipsis = ({ className, ...props }) => /* @__PURE__ */ jsx30(
  "span",
  {
    "aria-hidden": "true",
    className: cn("flex h-9 w-9 items-center justify-center", className),
    role: "presentation",
    ...props,
    children: /* @__PURE__ */ jsx30(MoreHorizontalIcon, { className: "h-4 w-4" })
  }
);

// src/components/Breadcrumb/BreadcrumbItem.tsx
import { forwardRef as forwardRef25 } from "react";
import { jsx as jsx31 } from "react/jsx-runtime";
var BreadcrumbItem = forwardRef25(function BreadcrumbItem2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx31("li", { className: cn("inline-flex items-center gap-1.5", className), ref, ...props });
});

// src/components/Breadcrumb/BreadcrumbLink.tsx
import { forwardRef as forwardRef26 } from "react";
import { Slot as Slot2 } from "@radix-ui/react-slot";
import { jsx as jsx32 } from "react/jsx-runtime";
var BreadcrumbLink = forwardRef26(function BreadcrumbLink2({ asChild, className, ...props }, ref) {
  const Comp = asChild ? Slot2 : "a";
  return /* @__PURE__ */ jsx32(Comp, { className: cn("transition-colors hover:text-foreground", className), ref, ...props });
});

// src/components/Breadcrumb/BreadcrumbList.tsx
import { forwardRef as forwardRef27 } from "react";
import { jsx as jsx33 } from "react/jsx-runtime";
var BreadcrumbList = forwardRef27(
  function BreadcrumbList2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx33(
      "ol",
      {
        className: cn(
          "flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5",
          className
        ),
        ref,
        ...props
      }
    );
  }
);

// src/components/Breadcrumb/BreadcrumbPage.tsx
import { forwardRef as forwardRef28 } from "react";
import { jsx as jsx34 } from "react/jsx-runtime";
var BreadcrumbPage = forwardRef28(
  function BreadcrumbPage2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx34(
      "span",
      {
        "aria-current": "page",
        "aria-disabled": "true",
        className: cn("font-normal text-foreground", className),
        ref,
        role: "link",
        ...props
      }
    );
  }
);

// src/components/Breadcrumb/BreadcrumbRoot.tsx
import { forwardRef as forwardRef29 } from "react";
import { jsx as jsx35 } from "react/jsx-runtime";
var BreadcrumbRoot = forwardRef29(function BreadcrumbRoot2({ ...props }, ref) {
  return /* @__PURE__ */ jsx35("nav", { "aria-label": "breadcrumb", ref, ...props });
});

// src/components/Breadcrumb/BreadcrumbSeparator.tsx
import "react";
import { ChevronRightIcon as ChevronRightIcon2 } from "lucide-react";
import { jsx as jsx36 } from "react/jsx-runtime";
var BreadcrumbSeparator = ({ children, className, ...props }) => /* @__PURE__ */ jsx36("li", { "aria-hidden": "true", className: cn("[&>svg]:size-3.5", className), role: "presentation", ...props, children: children ?? /* @__PURE__ */ jsx36(ChevronRightIcon2, {}) });

// src/components/Breadcrumb/Breadcrumb.tsx
var Breadcrumb = Object.assign(BreadcrumbRoot, {
  Ellipsis: BreadcrumbEllipsis,
  Item: BreadcrumbItem,
  Link: BreadcrumbLink,
  List: BreadcrumbList,
  Page: BreadcrumbPage,
  Separator: BreadcrumbSeparator
});

// src/components/Card/Card.tsx
import * as React10 from "react";
import { jsx as jsx37 } from "react/jsx-runtime";
var CardRoot = React10.forwardRef(function CardRoot2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx37(
    "div",
    {
      className: cn("rounded-xl border bg-card text-card-foreground shadow", className),
      "data-testid": "card",
      ref,
      ...props
    }
  );
});
var Card = Object.assign(CardRoot, {
  Content: ({ className, ...props }) => /* @__PURE__ */ jsx37("div", { className: cn("p-6 pt-0", className), ...props }),
  Description: ({ className, ...props }) => /* @__PURE__ */ jsx37("p", { className: cn("text-sm text-muted-foreground", className), ...props }),
  Footer: ({ className, ...props }) => {
    return /* @__PURE__ */ jsx37("div", { className: cn("flex items-center p-6 pt-0", className), ...props });
  },
  Header: ({ className, ...props }) => /* @__PURE__ */ jsx37("div", { className: cn("flex flex-col space-y-1.5 p-6", className), ...props }),
  Title: ({ children, className, ...props }) => /* @__PURE__ */ jsx37("h3", { className: cn("font-semibold leading-none tracking-tight", className), ...props, children })
});

// src/components/Checkbox/Checkbox.tsx
import { forwardRef as forwardRef31 } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon as CheckIcon2 } from "lucide-react";
import { jsx as jsx38 } from "react/jsx-runtime";
var Checkbox = forwardRef31(function Checkbox2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx38(
    CheckboxPrimitive.Root,
    {
      className: cn(
        "peer h-4 w-4 shrink-0 rounded-sm border border-primary shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
        className
      ),
      "data-testid": "checkbox",
      ref,
      ...props,
      children: /* @__PURE__ */ jsx38(CheckboxPrimitive.Indicator, { className: cn("flex items-center justify-center text-current"), children: /* @__PURE__ */ jsx38(CheckIcon2, { className: "h-4 w-4" }) })
    }
  );
});

// src/components/ClientTable/ClientTable.tsx
import { useState } from "react";
import "react";
import { toBasicISOString } from "@douglasneuroinformatics/libjs";
import { range } from "lodash-es";
import { ChevronDownIcon as ChevronDownIcon3 } from "lucide-react";

// src/components/Table/TableBody.tsx
import { forwardRef as forwardRef32 } from "react";
import { jsx as jsx39 } from "react/jsx-runtime";
var TableBody = forwardRef32(
  function TableBody2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx39("tbody", { className: cn("[&_tr:last-child]:border-0", className), ref, ...props });
  }
);

// src/components/Table/TableCaption.tsx
import { forwardRef as forwardRef33 } from "react";
import { jsx as jsx40 } from "react/jsx-runtime";
var TableCaption = forwardRef33(
  function TableCaption2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx40("caption", { className: cn("mt-4 text-sm text-muted-foreground", className), ref, ...props });
  }
);

// src/components/Table/TableCell.tsx
import { forwardRef as forwardRef34 } from "react";
import { jsx as jsx41 } from "react/jsx-runtime";
var TableCell = forwardRef34(
  function TableCell2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx41("td", { className: cn("px-6 py-3 align-middle [&:has([role=checkbox])]:pr-0", className), ref, ...props });
  }
);

// src/components/Table/TableFooter.tsx
import { forwardRef as forwardRef35 } from "react";
import { jsx as jsx42 } from "react/jsx-runtime";
var TableFooter = forwardRef35(
  function TableFooter2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx42(
      "tfoot",
      {
        className: cn("bg-muted/50 border-t font-medium [&>tr]:last:border-b-0", className),
        ref,
        ...props
      }
    );
  }
);

// src/components/Table/TableHead.tsx
import { forwardRef as forwardRef36 } from "react";
import { jsx as jsx43 } from "react/jsx-runtime";
var TableHead = forwardRef36(
  function TableHead2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx43(
      "th",
      {
        className: cn(
          "px-6 py-3 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
          className
        ),
        ref,
        ...props
      }
    );
  }
);

// src/components/Table/TableHeader.tsx
import { forwardRef as forwardRef37 } from "react";
import { jsx as jsx44 } from "react/jsx-runtime";
var TableHeader = forwardRef37(
  function TableHeader2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx44("thead", { className: cn("[&_tr]:border-b", className), ref, ...props });
  }
);

// src/components/Table/TableRoot.tsx
import { forwardRef as forwardRef38 } from "react";
import { jsx as jsx45 } from "react/jsx-runtime";
var TableRoot = forwardRef38(function TableRoot2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx45("div", { className: "relative w-full overflow-auto", children: /* @__PURE__ */ jsx45("table", { className: cn("w-full caption-bottom text-sm", className), ref, ...props }) });
});

// src/components/Table/TableRow.tsx
import { forwardRef as forwardRef39 } from "react";
import { jsx as jsx46 } from "react/jsx-runtime";
var TableRow = forwardRef39(function TableRow2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx46(
    "tr",
    {
      className: cn("hover:bg-muted/50 border-b transition-colors data-[state=selected]:bg-muted", className),
      ref,
      ...props
    }
  );
});

// src/components/Table/Table.tsx
var Table = Object.assign(TableRoot, {
  Body: TableBody,
  Caption: TableCaption,
  Cell: TableCell,
  Footer: TableFooter,
  Head: TableHead,
  Header: TableHeader,
  Row: TableRow
});

// src/components/ClientTable/ClientTablePagination.tsx
import { jsx as jsx47, jsxs as jsxs10 } from "react/jsx-runtime";
var ClientTablePagination = ({
  currentPage,
  firstEntry,
  lastEntry,
  pageCount,
  setCurrentPage,
  totalEntries
}) => {
  const { t } = useTranslation("libui");
  return /* @__PURE__ */ jsxs10("div", { className: "flex items-center justify-between py-3", children: [
    /* @__PURE__ */ jsx47("div", { className: "hidden sm:block", children: /* @__PURE__ */ jsx47("p", { className: "text-sm font-medium text-muted-foreground", children: `${firstEntry} - ${lastEntry} / ${totalEntries}` }) }),
    /* @__PURE__ */ jsxs10("div", { className: "flex flex-1 justify-between gap-3 sm:justify-end", children: [
      /* @__PURE__ */ jsx47(
        Button,
        {
          disabled: currentPage === 1,
          type: "button",
          variant: "outline",
          onClick: () => {
            setCurrentPage(currentPage - 1);
          },
          children: t("pagination.previous")
        }
      ),
      /* @__PURE__ */ jsx47(
        Button,
        {
          disabled: currentPage === pageCount,
          type: "button",
          variant: "outline",
          onClick: () => {
            setCurrentPage(currentPage + 1);
          },
          children: t("pagination.next")
        }
      )
    ] })
  ] });
};

// src/components/ClientTable/ClientTable.tsx
import { jsx as jsx48, jsxs as jsxs11 } from "react/jsx-runtime";
function defaultFormatter(value) {
  if (typeof value === "string") {
    return value;
  } else if (typeof value === "number") {
    return value.toFixed(2).toString();
  } else if (typeof value === "undefined") {
    return "NA";
  }
  if (value instanceof Date) {
    return toBasicISOString(value);
  }
  return JSON.stringify(value);
}
var ClientTable = ({
  className,
  columnDropdownOptions,
  columns,
  data,
  entriesPerPage = 10,
  minRows,
  noWrap,
  onEntryClick,
  ...props
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageCount = Math.max(Math.ceil(data.length / entriesPerPage), 1);
  const firstEntry = data.length === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1;
  const lastEntry = Math.min(firstEntry + entriesPerPage - 1, data.length);
  const currentEntries = data.slice(firstEntry - 1, lastEntry);
  const nRows = Math.max(currentEntries.length, minRows ?? -1);
  return /* @__PURE__ */ jsxs11("div", { className, ...props, children: [
    /* @__PURE__ */ jsx48("div", { className: "rounded-md border bg-card tracking-tight text-muted-foreground shadow-sm", children: /* @__PURE__ */ jsxs11(Table, { children: [
      /* @__PURE__ */ jsx48(Table.Header, { children: /* @__PURE__ */ jsx48(Table.Row, { children: columns.map((column, i) => /* @__PURE__ */ jsx48(Table.Head, { className: "whitespace-nowrap text-foreground", children: columnDropdownOptions ? /* @__PURE__ */ jsxs11(DropdownMenu, { children: [
        /* @__PURE__ */ jsxs11(DropdownMenu.Trigger, { className: "flex items-center justify-between gap-3", children: [
          /* @__PURE__ */ jsx48("span", { children: column.label }),
          /* @__PURE__ */ jsx48(ChevronDownIcon3, {})
        ] }),
        /* @__PURE__ */ jsx48(DropdownMenu.Content, { align: "start", children: /* @__PURE__ */ jsx48(DropdownMenu.Group, { children: columnDropdownOptions.map((option) => {
          const Icon = option.icon;
          return /* @__PURE__ */ jsxs11(
            DropdownMenu.Item,
            {
              onClick: () => {
                option.onSelection(column);
              },
              children: [
                Icon && /* @__PURE__ */ jsx48(Icon, { className: "mr-2", height: 16, width: 16 }),
                option.label
              ]
            },
            option.label
          );
        }) }) })
      ] }) : column.label }, i)) }) }),
      /* @__PURE__ */ jsx48(Table.Body, { children: range(nRows).map((i) => {
        const entry = currentEntries[i];
        const onClick = onEntryClick && entry ? () => onEntryClick(entry) : void 0;
        return /* @__PURE__ */ jsx48(
          Table.Row,
          {
            className: cn(onClick && "cursor-pointer hover:backdrop-brightness-95"),
            onClick,
            children: columns.map(({ field, formatter }, j) => {
              let value;
              if (!entry) {
                value = "NA";
              } else if (typeof field === "function") {
                value = field(entry);
              } else {
                value = entry[field];
              }
              const formattedValue = entry && formatter ? formatter(value) : defaultFormatter(value);
              return /* @__PURE__ */ jsx48(
                Table.Cell,
                {
                  className: cn(
                    "text-ellipsis leading-none",
                    !entry && "opacity-0",
                    // safari does not include borders if invisible
                    noWrap && "max-w-3xl overflow-hidden text-ellipsis whitespace-nowrap"
                  ),
                  children: formattedValue
                },
                j
              );
            })
          },
          i
        );
      }) })
    ] }) }),
    /* @__PURE__ */ jsx48(
      ClientTablePagination,
      {
        currentPage,
        firstEntry,
        lastEntry,
        pageCount,
        setCurrentPage,
        totalEntries: data.length
      }
    )
  ] });
};

// src/components/Collapsible/Collapsible.tsx
import { Content as Content4, Trigger as Trigger4 } from "@radix-ui/react-collapsible";

// src/components/Collapsible/CollapsibleRoot.tsx
import { forwardRef as forwardRef40 } from "react";
import { Root as Root6 } from "@radix-ui/react-collapsible";
import { jsx as jsx49 } from "react/jsx-runtime";
var CollapsibleRoot = forwardRef40(
  function CollapsibleRoot2(props, ref) {
    return /* @__PURE__ */ jsx49(Root6, { ref, ...props });
  }
);

// src/components/Collapsible/Collapsible.tsx
var Collapsible = Object.assign(CollapsibleRoot, {
  Content: Content4,
  Trigger: Trigger4
});

// src/components/Command/Command.tsx
import * as React13 from "react";
import { Command as CommandPrimitive7 } from "cmdk";

// src/components/Command/CommandEmpty.tsx
import { forwardRef as forwardRef41 } from "react";
import { Command as CommandPrimitive } from "cmdk";
import { jsx as jsx50 } from "react/jsx-runtime";
var CommandEmpty = forwardRef41(function CommandEmpty2(props, ref) {
  return /* @__PURE__ */ jsx50(CommandPrimitive.Empty, { className: "py-6 text-center text-sm", ref, ...props });
});

// src/components/Command/CommandGroup.tsx
import { forwardRef as forwardRef42 } from "react";
import { Command as CommandPrimitive2 } from "cmdk";
import { jsx as jsx51 } from "react/jsx-runtime";
var CommandGroup = forwardRef42(function CommandGroup2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx51(
    CommandPrimitive2.Group,
    {
      className: cn(
        "overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground",
        className
      ),
      ref,
      ...props
    }
  );
});

// src/components/Command/CommandInput.tsx
import { forwardRef as forwardRef43 } from "react";
import { Command as CommandPrimitive3 } from "cmdk";
import { SearchIcon } from "lucide-react";
import { jsx as jsx52, jsxs as jsxs12 } from "react/jsx-runtime";
var CommandInput = forwardRef43(function CommandInput2({ className, ...props }, ref) {
  return (
    // eslint-disable-next-line react/no-unknown-property
    /* @__PURE__ */ jsxs12("div", { className: "flex items-center border-b px-3", "cmdk-input-wrapper": "", children: [
      /* @__PURE__ */ jsx52(SearchIcon, { className: "mr-2 h-4 w-4 shrink-0 opacity-50" }),
      /* @__PURE__ */ jsx52(
        CommandPrimitive3.Input,
        {
          className: cn(
            "flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
            className
          ),
          ref,
          ...props
        }
      )
    ] })
  );
});

// src/components/Command/CommandItem.tsx
import { forwardRef as forwardRef44 } from "react";
import { Command as CommandPrimitive4 } from "cmdk";
import { jsx as jsx53 } from "react/jsx-runtime";
var CommandItem = forwardRef44(function CommandItem2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx53(
    CommandPrimitive4.Item,
    {
      className: cn(
        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
        className
      ),
      ref,
      ...props
    }
  );
});

// src/components/Command/CommandList.tsx
import { forwardRef as forwardRef45 } from "react";
import { Command as CommandPrimitive5 } from "cmdk";
import { jsx as jsx54 } from "react/jsx-runtime";
var CommandList = forwardRef45(function CommandList2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx54(
    CommandPrimitive5.List,
    {
      className: cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className),
      ref,
      ...props
    }
  );
});

// src/components/Command/CommandSeparator.tsx
import { forwardRef as forwardRef46 } from "react";
import { Command as CommandPrimitive6 } from "cmdk";
import { jsx as jsx55 } from "react/jsx-runtime";
var CommandSeparator = forwardRef46(function CommandSeparator2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx55(CommandPrimitive6.Separator, { className: cn("-mx-1 h-px bg-border", className), ref, ...props });
});

// src/components/Command/CommandShortcut.tsx
import "react";
import { jsx as jsx56 } from "react/jsx-runtime";
var CommandShortcut = ({ className, ...props }) => {
  return /* @__PURE__ */ jsx56("span", { className: cn("ml-auto text-xs tracking-widest text-muted-foreground", className), ...props });
};

// src/components/Command/Command.tsx
import { jsx as jsx57 } from "react/jsx-runtime";
var CommandRoot = React13.forwardRef(function Command({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx57(
    CommandPrimitive7,
    {
      className: cn(
        "flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
        className
      ),
      ref,
      ...props
    }
  );
});
var Command2 = Object.assign(CommandRoot, {
  Empty: CommandEmpty,
  Group: CommandGroup,
  Input: CommandInput,
  Item: CommandItem,
  List: CommandList,
  Separator: CommandSeparator,
  Shortcut: CommandShortcut
});

// src/components/ContextMenu/ContextMenu.tsx
import { Group as Group2, Portal as Portal5, RadioGroup as RadioGroup2, Root as Root7, Sub as Sub2, Trigger as Trigger5 } from "@radix-ui/react-context-menu";

// src/components/ContextMenu/ContextMenuCheckboxItem.tsx
import { forwardRef as forwardRef48 } from "react";
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import { CheckIcon as CheckIcon3 } from "lucide-react";
import { jsx as jsx58, jsxs as jsxs13 } from "react/jsx-runtime";
var ContextMenuCheckboxItem = forwardRef48(function ContextMenuCheckboxItem2({ checked, children, className, ...props }, ref) {
  return /* @__PURE__ */ jsxs13(
    ContextMenuPrimitive.CheckboxItem,
    {
      checked,
      className: cn(
        "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      ),
      ref,
      ...props,
      children: [
        /* @__PURE__ */ jsx58("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx58(ContextMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx58(CheckIcon3, { className: "h-4 w-4" }) }) }),
        children
      ]
    }
  );
});

// src/components/ContextMenu/ContextMenuContent.tsx
import { forwardRef as forwardRef49 } from "react";
import * as ContextMenuPrimitive2 from "@radix-ui/react-context-menu";
import { jsx as jsx59 } from "react/jsx-runtime";
var ContextMenuContent = forwardRef49(function ContextMenuContent2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx59(ContextMenuPrimitive2.Portal, { children: /* @__PURE__ */ jsx59(
    ContextMenuPrimitive2.Content,
    {
      className: cn(
        "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      ),
      ref,
      ...props
    }
  ) });
});

// src/components/ContextMenu/ContextMenuItem.tsx
import { forwardRef as forwardRef50 } from "react";
import * as ContextMenuPrimitive3 from "@radix-ui/react-context-menu";
import { jsx as jsx60 } from "react/jsx-runtime";
var ContextMenuItem = forwardRef50(function ContextMenuItem2({ className, inset, ...props }, ref) {
  return /* @__PURE__ */ jsx60(
    ContextMenuPrimitive3.Item,
    {
      className: cn(
        "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        inset && "pl-8",
        className
      ),
      ref,
      ...props
    }
  );
});

// src/components/ContextMenu/ContextMenuLabel.tsx
import { forwardRef as forwardRef51 } from "react";
import * as ContextMenuPrimitive4 from "@radix-ui/react-context-menu";
import { jsx as jsx61 } from "react/jsx-runtime";
var ContextMenuLabel = forwardRef51(function ContextMenuLabel2({ className, inset, ...props }, ref) {
  return /* @__PURE__ */ jsx61(
    ContextMenuPrimitive4.Label,
    {
      className: cn("px-2 py-1.5 text-sm font-semibold text-foreground", inset && "pl-8", className),
      ref,
      ...props
    }
  );
});

// src/components/ContextMenu/ContextMenuRadioItem.tsx
import { forwardRef as forwardRef52 } from "react";
import * as ContextMenuPrimitive5 from "@radix-ui/react-context-menu";
import { CircleIcon as CircleIcon2 } from "lucide-react";
import { jsx as jsx62, jsxs as jsxs14 } from "react/jsx-runtime";
var ContextMenuRadioItem = forwardRef52(function ContextMenuRadioItem2({ children, className, ...props }, ref) {
  return /* @__PURE__ */ jsxs14(
    ContextMenuPrimitive5.RadioItem,
    {
      className: cn(
        "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      ),
      ref,
      ...props,
      children: [
        /* @__PURE__ */ jsx62("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx62(ContextMenuPrimitive5.ItemIndicator, { children: /* @__PURE__ */ jsx62(CircleIcon2, { className: "fill-current", style: { height: 8, width: 8 } }) }) }),
        children
      ]
    }
  );
});

// src/components/ContextMenu/ContextMenuSeparator.tsx
import { forwardRef as forwardRef53 } from "react";
import * as ContextMenuPrimitive6 from "@radix-ui/react-context-menu";
import { jsx as jsx63 } from "react/jsx-runtime";
var ContextMenuSeparator = forwardRef53(function ContextMenuSeparator2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx63(ContextMenuPrimitive6.Separator, { className: cn("-mx-1 my-1 h-px bg-border", className), ref, ...props });
});

// src/components/ContextMenu/ContextMenuShortcut.tsx
import "react";
import { jsx as jsx64 } from "react/jsx-runtime";
var ContextMenuShortcut = ({ className, ...props }) => {
  return /* @__PURE__ */ jsx64("span", { className: cn("ml-auto text-xs tracking-widest text-muted-foreground", className), ...props });
};

// src/components/ContextMenu/ContextMenuSubContent.tsx
import { forwardRef as forwardRef54 } from "react";
import * as ContextMenuPrimitive7 from "@radix-ui/react-context-menu";
import { jsx as jsx65 } from "react/jsx-runtime";
var ContextMenuSubContent = forwardRef54(function ContextMenuSubContent2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx65(
    ContextMenuPrimitive7.SubContent,
    {
      className: cn(
        "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      ),
      ref,
      ...props
    }
  );
});

// src/components/ContextMenu/ContextMenuSubTrigger.tsx
import { forwardRef as forwardRef55 } from "react";
import * as ContextMenuPrimitive8 from "@radix-ui/react-context-menu";
import { ChevronRightIcon as ChevronRightIcon3 } from "lucide-react";
import { jsx as jsx66, jsxs as jsxs15 } from "react/jsx-runtime";
var ContextMenuSubTrigger = forwardRef55(function ContextMenuSubTrigger2({ children, className, inset, ...props }, ref) {
  return /* @__PURE__ */ jsxs15(
    ContextMenuPrimitive8.SubTrigger,
    {
      className: cn(
        "flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
        inset && "pl-8",
        className
      ),
      ref,
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx66(ChevronRightIcon3, { className: "ml-auto h-4 w-4" })
      ]
    }
  );
});

// src/components/ContextMenu/ContextMenu.tsx
var ContextMenu = Object.assign(Root7.bind(null), {
  CheckboxItem: ContextMenuCheckboxItem,
  Content: ContextMenuContent,
  Group: Group2,
  Item: ContextMenuItem,
  Label: ContextMenuLabel,
  Portal: Portal5,
  RadioGroup: RadioGroup2,
  RadioItem: ContextMenuRadioItem,
  Separator: ContextMenuSeparator,
  Shortcut: ContextMenuShortcut,
  Sub: Sub2,
  SubContent: ContextMenuSubContent,
  SubTrigger: ContextMenuSubTrigger,
  Trigger: Trigger5
});

// src/components/CopyButton/CopyButton.tsx
import { useState as useState2 } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ClipboardCheckIcon, ClipboardListIcon } from "lucide-react";
import { match } from "ts-pattern";
import { jsx as jsx67 } from "react/jsx-runtime";
var CopyButton = ({
  size,
  text,
  variant
}) => {
  const [state, setState] = useState2("READY");
  return /* @__PURE__ */ jsx67(
    Button,
    {
      size: size ?? "icon",
      type: "button",
      variant,
      onClick: () => {
        if (state === "READY") {
          navigator.clipboard.writeText(text).then(() => setState("SUCCESS")).catch(console.error);
        }
      },
      onMouseLeave: () => {
        setState("READY");
      },
      children: /* @__PURE__ */ jsx67(AnimatePresence, { mode: "popLayout", children: /* @__PURE__ */ jsx67(
        motion.div,
        {
          animate: { opacity: 1 },
          exit: { opacity: 0 },
          initial: { opacity: 0 },
          transition: { duration: 0.1 },
          children: match(state).with("READY", () => /* @__PURE__ */ jsx67(ClipboardListIcon, {})).with("SUCCESS", () => /* @__PURE__ */ jsx67(ClipboardCheckIcon, {})).exhaustive()
        },
        state
      ) })
    }
  );
};

// src/components/DatePicker/DatePicker.tsx
import { useReducer, useState as useState3 } from "react";
import * as React16 from "react";
import { AnimatePresence as AnimatePresence3, motion as motion3 } from "framer-motion";

// src/components/DatePicker/Calendar.tsx
import { forwardRef as forwardRef56 } from "react";
import { AnimatePresence as AnimatePresence2, motion as motion2 } from "framer-motion";
import { range as range2 } from "lodash-es";
import { jsx as jsx68, jsxs as jsxs16 } from "react/jsx-runtime";
var CALENDAR_ANIMATION_DURATION = 0.2;
var Calendar = forwardRef56(function Calendar2(props, ref) {
  const { t } = useTranslation("libui");
  const firstDay = new Date(props.year, props.month).getDay();
  const lastDay = new Date(props.year, props.month + 1, 0).getDate();
  const days = range2(1, lastDay + 1);
  const daysOfWeek = [
    t("days.sunday"),
    t("days.monday"),
    t("days.tuesday"),
    t("days.wednesday"),
    t("days.thursday"),
    t("days.friday"),
    t("days.saturday")
  ];
  return /* @__PURE__ */ jsx68(AnimatePresence2, { initial: false, mode: "wait", children: /* @__PURE__ */ jsx68(
    motion2.div,
    {
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -20 },
      initial: { opacity: 0, x: 20 },
      transition: { duration: CALENDAR_ANIMATION_DURATION },
      children: /* @__PURE__ */ jsxs16("div", { className: "grid h-56 w-56 grid-cols-7 text-sm", ref, children: [
        daysOfWeek.map((label) => /* @__PURE__ */ jsx68("div", { className: "flex h-8 w-8 items-center justify-center text-muted-foreground", children: label.charAt(0).toUpperCase() }, label)),
        /* @__PURE__ */ jsx68("div", { style: { gridColumn: `span ${firstDay} / span ${firstDay}` } }),
        days.map((day) => /* @__PURE__ */ jsx68(
          "button",
          {
            className: "dark:hover:bg-extra-muted flex h-8 w-8 items-center justify-center rounded-md hover:bg-slate-200 dark:hover:bg-slate-700",
            tabIndex: -1,
            type: "button",
            onClick: () => {
              props.onSelection(new Date(props.year, props.month, day));
            },
            children: day
          },
          day
        ))
      ] })
    },
    `${props.year}-${props.month}`
  ) });
});

// src/components/DatePicker/YearSelector.tsx
import { useEffect, useRef } from "react";
import { range as range3 } from "lodash-es";

// src/components/ScrollArea/ScrollArea.tsx
import { forwardRef as forwardRef58 } from "react";
import { Corner, Root as Root8, Viewport } from "@radix-ui/react-scroll-area";

// src/components/ScrollArea/ScrollBar.tsx
import { forwardRef as forwardRef57 } from "react";
import { ScrollAreaScrollbar, ScrollAreaThumb } from "@radix-ui/react-scroll-area";
import { jsx as jsx69 } from "react/jsx-runtime";
var ScrollBar = forwardRef57(function ScrollBar2({ className, orientation = "vertical", ...props }, ref) {
  return /* @__PURE__ */ jsx69(
    ScrollAreaScrollbar,
    {
      className: cn(
        "flex touch-none select-none transition-colors",
        orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
        orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-[1px]",
        className
      ),
      orientation,
      ref,
      ...props,
      children: /* @__PURE__ */ jsx69(ScrollAreaThumb, { className: "relative flex-1 rounded-full bg-border" })
    }
  );
});

// src/components/ScrollArea/ScrollArea.tsx
import { jsx as jsx70, jsxs as jsxs17 } from "react/jsx-runtime";
var ScrollArea = forwardRef58(
  function ScrollArea2({ children, className, ...props }, ref) {
    return /* @__PURE__ */ jsxs17(Root8, { className: cn("relative overflow-hidden", className), "data-testid": "scroll-area", ref, ...props, children: [
      /* @__PURE__ */ jsx70(Viewport, { className: "h-full w-full rounded-[inherit]", children }),
      /* @__PURE__ */ jsx70(ScrollBar, {}),
      /* @__PURE__ */ jsx70(Corner, {})
    ] });
  }
);

// src/components/DatePicker/YearSelector.tsx
import { jsx as jsx71 } from "react/jsx-runtime";
var YearSelector = (props) => {
  const selectedRef = useRef(null);
  const currentYear = (/* @__PURE__ */ new Date()).getFullYear();
  const years = Array.from(range3(currentYear - 100, currentYear + 8)).reverse();
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: "center" });
    }
  }, []);
  return /* @__PURE__ */ jsx71(ScrollArea, { className: "h-56 w-56", children: /* @__PURE__ */ jsx71("div", { className: "grid grid-cols-3 gap-x-2 gap-y-1 text-sm text-muted-foreground", children: years.map((year) => /* @__PURE__ */ jsx71("div", { className: "flex h-7 items-center justify-center", children: /* @__PURE__ */ jsx71(
    "button",
    {
      className: cn(
        "h-full w-full rounded-md hover:bg-slate-200 dark:hover:bg-slate-700",
        year === props.selected.getFullYear() && "bg-primary text-primary-foreground hover:bg-primary dark:hover:bg-primary"
      ),
      ref: year === props.selected.getFullYear() ? selectedRef : null,
      tabIndex: -1,
      type: "button",
      onClick: () => {
        props.onSelection(new Date(year, 0));
      },
      children: year
    }
  ) }, year)) }) });
};

// src/components/DatePicker/DatePicker.tsx
import { jsx as jsx72, jsxs as jsxs18 } from "react/jsx-runtime";
var MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december"
];
var reducer = (previousDate, action) => {
  const newDate = new Date(previousDate.valueOf());
  switch (action.type) {
    case "increment":
      newDate.setMonth(newDate.getMonth() + 1);
      break;
    case "decrement":
      newDate.setMonth(newDate.getMonth() - 1);
      break;
    case "set-year":
      newDate.setFullYear(action.value);
  }
  return newDate;
};
var DatePicker = React16.forwardRef(function DatePicker2({ onSelection, ...props }, ref) {
  const [date, dispatch] = useReducer(reducer, /* @__PURE__ */ new Date());
  const [showYearSelector, setShowYearSelector] = useState3(false);
  const { t } = useTranslation("libui");
  const [canSetMonth, setCanSetMonth] = useState3(true);
  const monthName = t(`months.${MONTHS[date.getMonth()]}`);
  const handleYearSelection = (date2) => {
    dispatch({ type: "set-year", value: date2.getFullYear() });
    setShowYearSelector(false);
  };
  return /* @__PURE__ */ jsxs18(Card, { className: "w-fit p-3", "data-testid": "datepicker", ref, ...props, children: [
    /* @__PURE__ */ jsxs18("div", { className: "mb-3 flex items-center justify-between px-1", children: [
      /* @__PURE__ */ jsxs18("div", { className: "flex items-center", children: [
        /* @__PURE__ */ jsx72("span", { className: "mx-1 text-sm font-medium tracking-tight", children: `${monthName} ${date.getFullYear()}` }),
        /* @__PURE__ */ jsx72(
          ArrowToggle,
          {
            className: "flex items-center justify-center rounded-full p-1 hover:bg-slate-200 dark:hover:bg-slate-700",
            isToggled: showYearSelector,
            position: "up",
            rotation: 180,
            tabIndex: -1,
            onClick: () => {
              setShowYearSelector(!showYearSelector);
            }
          }
        )
      ] }),
      /* @__PURE__ */ jsxs18("div", { className: cn("flex", { hidden: showYearSelector }), children: [
        /* @__PURE__ */ jsx72(
          ArrowToggle,
          {
            className: "flex items-center justify-center rounded-full p-1 hover:bg-slate-200 dark:hover:bg-slate-700",
            position: "left",
            tabIndex: -1,
            onClick: () => {
              if (canSetMonth) {
                setCanSetMonth(false);
                dispatch({ type: "decrement" });
                setTimeout(() => {
                  setCanSetMonth(true);
                }, CALENDAR_ANIMATION_DURATION * 2e3);
              }
            }
          }
        ),
        /* @__PURE__ */ jsx72(
          ArrowToggle,
          {
            className: "flex items-center justify-center rounded-full p-1 hover:bg-slate-200 dark:hover:bg-slate-700",
            position: "right",
            tabIndex: -1,
            onClick: () => {
              if (canSetMonth) {
                setCanSetMonth(false);
                dispatch({ type: "increment" });
                setTimeout(() => {
                  setCanSetMonth(true);
                }, CALENDAR_ANIMATION_DURATION * 2e3);
              }
            }
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsx72("div", { children: /* @__PURE__ */ jsx72(AnimatePresence3, { initial: false, mode: "wait", children: showYearSelector ? /* @__PURE__ */ jsx72(
      motion3.div,
      {
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 10 },
        initial: { opacity: 0, y: 10 },
        transition: { duration: 0.2 },
        children: /* @__PURE__ */ jsx72(YearSelector, { selected: date, onSelection: handleYearSelection })
      },
      0
    ) : /* @__PURE__ */ jsx72(
      motion3.div,
      {
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -10 },
        initial: { opacity: 0, y: -10 },
        transition: { duration: 0.2 },
        children: /* @__PURE__ */ jsx72(Calendar, { month: date.getMonth(), year: date.getFullYear(), onSelection })
      },
      1
    ) }) })
  ] });
});

// src/components/Dialog/Dialog.tsx
import { Root as Root9, Trigger as Trigger6 } from "@radix-ui/react-dialog";

// src/components/Dialog/DialogBody.tsx
import "react";
import { jsx as jsx73 } from "react/jsx-runtime";
var DialogBody = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx73("div", { className: cn("py-4", className), ...props, children });
};

// src/components/Dialog/DialogContent.tsx
import { forwardRef as forwardRef61 } from "react";
import { Close, Content as Content6, Portal as Portal6 } from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

// src/components/Dialog/DialogOverlay.tsx
import { forwardRef as forwardRef60 } from "react";
import { Overlay as Overlay2 } from "@radix-ui/react-dialog";
import { jsx as jsx74 } from "react/jsx-runtime";
var DialogOverlay = forwardRef60(function DialogOverlay2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx74(
    Overlay2,
    {
      className: cn(
        "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      ),
      ref,
      ...props
    }
  );
});

// src/components/Dialog/DialogContent.tsx
import { jsx as jsx75, jsxs as jsxs19 } from "react/jsx-runtime";
var DialogContent = forwardRef61(function DialogContent2({ children, className, ...props }, ref) {
  return /* @__PURE__ */ jsxs19(Portal6, { children: [
    /* @__PURE__ */ jsx75(DialogOverlay, {}),
    /* @__PURE__ */ jsxs19(
      Content6,
      {
        className: cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        ),
        ref,
        ...props,
        children: [
          children,
          /* @__PURE__ */ jsx75(Close, { className: "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground", children: /* @__PURE__ */ jsx75(XIcon, { className: "h-4 w-4" }) })
        ]
      }
    )
  ] });
});

// src/components/Dialog/DialogDescription.tsx
import { forwardRef as forwardRef62 } from "react";
import { Description as Description2 } from "@radix-ui/react-dialog";
import { jsx as jsx76 } from "react/jsx-runtime";
var DialogDescription = forwardRef62(function DialogDescription2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx76(Description2, { className: cn("text-sm text-muted-foreground", className), ref, ...props });
});

// src/components/Dialog/DialogFooter.tsx
import "react";
import { jsx as jsx77 } from "react/jsx-runtime";
var DialogFooter = ({ className, ...props }) => /* @__PURE__ */ jsx77("div", { className: cn("flex flex-col gap-x-2 gap-y-0.5 sm:flex-row", className), ...props });

// src/components/Dialog/DialogHeader.tsx
import "react";
import { jsx as jsx78 } from "react/jsx-runtime";
var DialogHeader = ({ className, ...props }) => /* @__PURE__ */ jsx78("div", { className: cn("flex flex-col space-y-1.5 text-center sm:text-left", className), ...props });

// src/components/Dialog/DialogTitle.tsx
import { forwardRef as forwardRef63 } from "react";
import { Title as Title2 } from "@radix-ui/react-dialog";
import { jsx as jsx79 } from "react/jsx-runtime";
var DialogTitle = forwardRef63(
  function DialogTitle2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx79(Title2, { className: cn("text-lg font-semibold leading-none tracking-tight", className), ref, ...props });
  }
);

// src/components/Dialog/Dialog.tsx
var Dialog = Object.assign(Root9.bind(null), {
  Body: DialogBody,
  Content: DialogContent,
  Description: DialogDescription,
  Footer: DialogFooter,
  Header: DialogHeader,
  Title: DialogTitle,
  Trigger: Trigger6
});

// src/components/Drawer/Drawer.tsx
import "react";
import { Drawer as DrawerPrimitive4 } from "vaul";

// src/components/Drawer/DrawerContent.tsx
import { forwardRef as forwardRef64 } from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { jsx as jsx80, jsxs as jsxs20 } from "react/jsx-runtime";
var DrawerContent = forwardRef64(function DrawerContent2({ children, className, ...props }, ref) {
  return /* @__PURE__ */ jsxs20(DrawerPrimitive.Portal, { children: [
    /* @__PURE__ */ jsx80(DrawerPrimitive.Overlay, { className: cn("fixed inset-0 z-50 bg-black/80", className), ref, ...props }),
    /* @__PURE__ */ jsxs20(
      DrawerPrimitive.Content,
      {
        className: cn(
          "fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-[10px] border bg-background",
          className
        ),
        ref,
        ...props,
        children: [
          /* @__PURE__ */ jsx80("div", { className: "mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" }),
          children
        ]
      }
    )
  ] });
});

// src/components/Drawer/DrawerDescription.tsx
import { forwardRef as forwardRef65 } from "react";
import { Drawer as DrawerPrimitive2 } from "vaul";
import { jsx as jsx81 } from "react/jsx-runtime";
var DrawerDescription = forwardRef65(function DrawerDescription2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx81(DrawerPrimitive2.Description, { className: cn("text-sm text-muted-foreground", className), ref, ...props });
});

// src/components/Drawer/DrawerFooter.tsx
import "react";
import { jsx as jsx82 } from "react/jsx-runtime";
var DrawerFooter = ({ className, ...props }) => /* @__PURE__ */ jsx82("div", { className: cn("mt-auto flex flex-col gap-2 p-4", className), ...props });

// src/components/Drawer/DrawerHeader.tsx
import "react";
import { jsx as jsx83 } from "react/jsx-runtime";
var DrawerHeader = ({ className, ...props }) => /* @__PURE__ */ jsx83("div", { className: cn("grid gap-1.5 p-4 text-center sm:text-left", className), ...props });

// src/components/Drawer/DrawerTitle.tsx
import { forwardRef as forwardRef66 } from "react";
import { Drawer as DrawerPrimitive3 } from "vaul";
import { jsx as jsx84 } from "react/jsx-runtime";
var DrawerTitle = forwardRef66(function DrawerTitle2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx84(
    DrawerPrimitive3.Title,
    {
      className: cn("text-lg font-semibold leading-none tracking-tight", className),
      ref,
      ...props
    }
  );
});

// src/components/Drawer/Drawer.tsx
import { jsx as jsx85 } from "react/jsx-runtime";
var DrawerRoot = ({ shouldScaleBackground = true, ...props }) => /* @__PURE__ */ jsx85(DrawerPrimitive4.Root, { shouldScaleBackground, ...props });
var Drawer = Object.assign(DrawerRoot, {
  Close: DrawerPrimitive4.Close,
  Content: DrawerContent,
  Description: DrawerDescription,
  Footer: DrawerFooter,
  Header: DrawerHeader,
  Title: DrawerTitle,
  Trigger: DrawerPrimitive4.Trigger
});

// src/components/ErrorBoundary/ErrorBoundary.tsx
import "react";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";

// src/components/ErrorFallback/ErrorFallback.tsx
import { useEffect as useEffect2 } from "react";
import { jsx as jsx86, jsxs as jsxs21 } from "react/jsx-runtime";
var ErrorFallback = ({ error }) => {
  useEffect2(() => {
    console.error(error);
  }, [error]);
  return /* @__PURE__ */ jsxs21("div", { className: "flex min-h-screen flex-col items-center justify-center gap-1 p-3 text-center", children: [
    /* @__PURE__ */ jsx86("h1", { className: "text-sm font-semibold uppercase tracking-wide text-muted-foreground", children: "Unexpected Error" }),
    /* @__PURE__ */ jsx86("h3", { className: "text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl", children: "Something Went Wrong" }),
    /* @__PURE__ */ jsx86("p", { className: "mt-2 max-w-prose text-sm text-muted-foreground sm:text-base", children: "We apologize for the inconvenience. Please contact us for further assistance." }),
    /* @__PURE__ */ jsx86("div", { className: "mt-6", children: /* @__PURE__ */ jsxs21(
      "button",
      {
        className: "text-sky-800 underline-offset-4 hover:text-sky-700 hover:underline dark:text-sky-200 dark:hover:text-sky-300",
        type: "button",
        onClick: () => {
          window.location.assign(window.location.origin);
        },
        children: [
          "Reload Page",
          /* @__PURE__ */ jsx86("span", { "aria-hidden": "true", children: " \u2192" })
        ]
      }
    ) })
  ] });
};

// src/components/ErrorBoundary/ErrorBoundary.tsx
import { jsx as jsx87 } from "react/jsx-runtime";
var ErrorBoundary = ({ children }) => {
  return /* @__PURE__ */ jsx87(ReactErrorBoundary, { FallbackComponent: ErrorFallback, children });
};

// src/components/FileDropzone/FileDropzone.tsx
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { jsx as jsx88, jsxs as jsxs22 } from "react/jsx-runtime";
var FileDropzone = ({ acceptedFileTypes, file, setFile }) => {
  const { t } = useTranslation();
  const handleDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      for (const { errors, file: file2 } of rejectedFiles) {
        console.error(errors, file2);
      }
      setFile(acceptedFiles[0]);
    },
    [setFile]
  );
  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: acceptedFileTypes,
    maxFiles: 1,
    onDrop: handleDrop
  });
  return /* @__PURE__ */ jsxs22("div", { "data-testid": "dropzone", ...getRootProps(), children: [
    /* @__PURE__ */ jsx88("p", { className: "mt-1 border border-dashed p-4 text-center text-sm", "data-testid": "dropzoneText", children: file ? file.name : isDragActive ? t({
      en: "File to upload",
      fr: "fichier \xE0 t\xE9l\xE9charger"
    }) : t({
      en: "Drag'n'drop files or click on box to upload",
      fr: "Glissez-d\xE9posez les fichiers ou cliquez sur la case pour les t\xE9l\xE9charger"
    }) }),
    /* @__PURE__ */ jsx88("input", { ...getInputProps() })
  ] });
};

// src/components/Form/Form.tsx
import { useEffect as useEffect9, useState as useState8 } from "react";
import "react";
import { get, set } from "lodash-es";
import { twMerge } from "tailwind-merge";
import "zod";

// src/components/Heading/Heading.tsx
import { jsx as jsx89 } from "react/jsx-runtime";
var Heading = ({ children, className, variant }) => {
  switch (variant) {
    case "h1":
      return /* @__PURE__ */ jsx89("h1", { className: cn("text-3xl font-bold tracking-tight", className), children });
    case "h2":
      return /* @__PURE__ */ jsx89("h2", { className: cn("text-2xl font-semibold tracking-tight", className), children });
    case "h3":
      return /* @__PURE__ */ jsx89("h3", { className: cn("text-xl font-semibold tracking-tight", className), children });
    case "h4":
      return /* @__PURE__ */ jsx89("h4", { className: cn("text-lg font-semibold tracking-tight", className), children });
    case "h5":
      return /* @__PURE__ */ jsx89("h5", { className: cn("text-base font-semibold tracking-tight", className), children });
    default:
      throw new Error(`Unhandled heading variant: ${variant}`);
  }
};

// src/components/Form/ErrorMessage.tsx
import "react";
import { CircleAlertIcon } from "lucide-react";
import { jsx as jsx90, jsxs as jsxs23 } from "react/jsx-runtime";
var ErrorMessage = ({ error }) => {
  return error ? /* @__PURE__ */ jsx90("div", { className: "space-y-1.5", children: error.map((message) => /* @__PURE__ */ jsxs23("div", { className: "flex w-full items-center text-sm font-medium text-destructive", children: [
    /* @__PURE__ */ jsx90(CircleAlertIcon, { className: "mr-1", style: { strokeWidth: "2px" } }),
    /* @__PURE__ */ jsx90("span", { children: message })
  ] }, message)) ?? null }) : null;
};

// src/components/Form/FieldsComponent.tsx
import "react";

// src/components/Form/DynamicField.tsx
import { useEffect as useEffect8, useMemo as useMemo2, useState as useState7 } from "react";
import "react";
import { pick } from "lodash-es";

// src/components/Form/StaticField.tsx
import { useCallback as useCallback3 } from "react";
import "react";
import { match as match7 } from "ts-pattern";

// src/components/Form/NumberRecordField.tsx
import { useEffect as useEffect3 } from "react";

// src/components/Form/NumberField/NumberField.tsx
import { match as match2 } from "ts-pattern";

// src/components/Form/NumberField/NumberFieldInput.tsx
import { useRef as useRef2, useState as useState4 } from "react";
import "react";
import { parseNumber } from "@douglasneuroinformatics/libjs";

// src/components/Input/Input.tsx
import * as React25 from "react";
import { jsx as jsx91 } from "react/jsx-runtime";
var Input = React25.forwardRef(function Input2({ className, type, ...props }, ref) {
  return /* @__PURE__ */ jsx91(
    "input",
    {
      autoComplete: "off",
      className: cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground placeholder:opacity-80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      "data-testid": "input",
      ref,
      type,
      ...props
    }
  );
});

// src/components/Label/Label.tsx
import { forwardRef as forwardRef68 } from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva as cva3 } from "class-variance-authority";
import { jsx as jsx92 } from "react/jsx-runtime";
var labelVariants = cva3(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 aria-disabled:cursor-not-allowed aria-disabled:opacity-70"
);
var Label3 = forwardRef68(function Label4({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx92(LabelPrimitive.Root, { className: cn(labelVariants(), className), ref, ...props });
});

// src/components/Form/FieldGroup/FieldGroupDescription.tsx
import "react";
import { CircleHelpIcon } from "lucide-react";

// src/components/Popover/Popover.tsx
import { Root as PopoverRoot, Trigger as PopoverTrigger } from "@radix-ui/react-popover";

// src/components/Popover/PopoverContent.tsx
import * as React26 from "react";
import { Content as Content7, Portal as Portal7 } from "@radix-ui/react-popover";
import { jsx as jsx93 } from "react/jsx-runtime";
var PopoverContent = React26.forwardRef(
  function PopoverContent2({ align = "center", asChild, autofocus = true, className, collisionPadding = 0, sideOffset = 4, ...props }, ref) {
    return /* @__PURE__ */ jsx93(Portal7, { children: /* @__PURE__ */ jsx93(
      Content7,
      {
        align,
        asChild,
        className: cn(
          "z-50 w-72 rounded-md border bg-popover px-3 py-1.5 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        ),
        collisionPadding,
        ref,
        sideOffset,
        onOpenAutoFocus: autofocus === false ? (event) => event.preventDefault() : void 0,
        ...props
      }
    ) });
  }
);

// src/components/Popover/Popover.tsx
var Popover = Object.assign(PopoverRoot.bind(null), {
  Content: PopoverContent,
  Trigger: PopoverTrigger
});

// src/components/Form/FieldGroup/FieldGroupDescription.tsx
import { jsx as jsx94, jsxs as jsxs24 } from "react/jsx-runtime";
var FieldGroupDescription = ({ description }) => description ? /* @__PURE__ */ jsxs24(Popover, { children: [
  /* @__PURE__ */ jsx94(Popover.Trigger, { tabIndex: -1, children: /* @__PURE__ */ jsx94(CircleHelpIcon, { className: "text-muted-foreground" }) }),
  /* @__PURE__ */ jsx94(Popover.Content, { className: "text-sm text-muted-foreground", children: /* @__PURE__ */ jsx94("p", { children: description }) })
] }) : null;

// src/components/Form/FieldGroup/FieldGroupRoot.tsx
import "react";
import { jsx as jsx95 } from "react/jsx-runtime";
var FieldGroupRoot = ({ children, name }) => /* @__PURE__ */ jsx95("div", { className: "flex flex-col gap-3 @container", "data-field-group": name, children });

// src/components/Form/FieldGroup/FieldGroupRow.tsx
import "react";
import { jsx as jsx96 } from "react/jsx-runtime";
var FieldGroupRow = ({ children }) => /* @__PURE__ */ jsx96("div", { className: "relative flex items-center gap-2", children });

// src/components/Form/FieldGroup/FieldGroup.tsx
var FieldGroup = Object.assign(FieldGroupRoot, {
  Description: FieldGroupDescription,
  Error: ErrorMessage,
  Row: FieldGroupRow
});

// src/components/Form/NumberField/NumberFieldInput.tsx
import { jsx as jsx97, jsxs as jsxs25 } from "react/jsx-runtime";
var NumberFieldInput = ({
  description,
  disabled,
  error,
  label,
  max = Number.MAX_SAFE_INTEGER,
  min = Number.MIN_SAFE_INTEGER,
  name,
  readOnly,
  setValue,
  value
}) => {
  const inputValueRef = useRef2(value?.toString() ?? "");
  const [inputKey, setInputKey] = useState4(0);
  const handleChange = (event) => {
    let newValue = value;
    if (/^[+-]?$/.test(event.target.value)) {
      newValue = void 0;
      inputValueRef.current = event.target.value;
    } else {
      const parsedValue = parseNumber(event.target.value);
      if (parsedValue >= min && parsedValue <= max) {
        newValue = parsedValue;
        inputValueRef.current = event.target.value;
      }
    }
    if (value === newValue) {
      setInputKey(inputKey + 1);
    } else {
      setValue(newValue);
    }
  };
  return /* @__PURE__ */ jsxs25(FieldGroup, { name, children: [
    /* @__PURE__ */ jsxs25(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx97(Label3, { children: label }),
      /* @__PURE__ */ jsx97(FieldGroup.Description, { description })
    ] }),
    /* @__PURE__ */ jsx97(
      Input,
      {
        disabled: disabled || readOnly,
        max,
        min,
        name,
        type: "text",
        value: inputValueRef.current,
        onChange: handleChange
      }
    ),
    /* @__PURE__ */ jsx97(FieldGroup.Error, { error })
  ] });
};

// src/components/RadioGroup/RadioGroup.tsx
import * as React31 from "react";
import * as RadioGroupPrimitive2 from "@radix-ui/react-radio-group";

// src/components/RadioGroup/RadioGroupItem.tsx
import { forwardRef as forwardRef70 } from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { CircleIcon as CircleIcon3 } from "lucide-react";
import { jsx as jsx98 } from "react/jsx-runtime";
var RadioGroupItem = forwardRef70(function RadioGroupItem2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx98(
    RadioGroupPrimitive.Item,
    {
      className: cn(
        "flex aspect-square h-4 w-4 items-center justify-center rounded-full border border-primary text-primary shadow focus:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ref,
      ...props,
      children: /* @__PURE__ */ jsx98(RadioGroupPrimitive.Indicator, { asChild: true, children: /* @__PURE__ */ jsx98(
        CircleIcon3,
        {
          className: "fill-current text-current",
          style: { height: "0.625rem", strokeWidth: "2px", width: "0.625rem" }
        }
      ) })
    }
  );
});

// src/components/RadioGroup/RadioGroup.tsx
import { jsx as jsx99 } from "react/jsx-runtime";
var RadioGroupRoot = React31.forwardRef(function RadioGroup3({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx99(RadioGroupPrimitive2.Root, { className: cn("grid gap-2", className), "data-testid": "radio-group", ...props, ref });
});
var RadioGroup4 = Object.assign(RadioGroupRoot, {
  Item: RadioGroupItem
});

// src/components/Form/NumberField/NumberFieldRadio.tsx
import { jsx as jsx100, jsxs as jsxs26 } from "react/jsx-runtime";
var NumberFieldRadio = ({
  description,
  disableAutoPrefix,
  disabled,
  error,
  label,
  name,
  options,
  readOnly,
  setValue,
  value
}) => {
  const optionsCount = Object.keys(options).length;
  return /* @__PURE__ */ jsxs26(FieldGroup, { name, children: [
    /* @__PURE__ */ jsxs26(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx100(Label3, { children: label }),
      /* @__PURE__ */ jsx100(FieldGroup.Description, { description })
    ] }),
    /* @__PURE__ */ jsx100(
      RadioGroup4,
      {
        className: cn(
          "flex",
          optionsCount > 5 ? "flex-col" : "flex-col @3xl:flex-row @3xl:items-center @3xl:justify-between"
        ),
        name,
        value: value?.toString() ?? "",
        onValueChange: (value2) => setValue(parseInt(value2)),
        children: Object.keys(options).map((val) => parseInt(val)).toSorted((a, b) => a - b).map((val) => {
          const text = (disableAutoPrefix ? "" : `${val} - `) + options[val];
          return /* @__PURE__ */ jsxs26("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx100(RadioGroup4.Item, { disabled: disabled || readOnly, id: `${name}-${val}`, value: val.toString() }),
            /* @__PURE__ */ jsx100(
              Label3,
              {
                "aria-disabled": disabled || readOnly,
                className: "font-normal text-muted-foreground",
                htmlFor: `${name}-${val}`,
                children: text
              }
            )
          ] }, val);
        })
      }
    ),
    /* @__PURE__ */ jsx100(FieldGroup.Error, { error })
  ] });
};

// src/components/Select/Select.tsx
import * as SelectPrimitive8 from "@radix-ui/react-select";

// src/components/Select/SelectContent.tsx
import { forwardRef as forwardRef74 } from "react";
import * as SelectPrimitive3 from "@radix-ui/react-select";

// src/components/Select/SelectScrollDownButton.tsx
import { forwardRef as forwardRef72 } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { ChevronDownIcon as ChevronDownIcon4 } from "lucide-react";
import { jsx as jsx101 } from "react/jsx-runtime";
var SelectScrollDownButton = forwardRef72(function SelectScrollDownButton2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx101(
    SelectPrimitive.ScrollDownButton,
    {
      className: cn("flex cursor-default items-center justify-center py-1", className),
      ref,
      ...props,
      children: /* @__PURE__ */ jsx101(ChevronDownIcon4, {})
    }
  );
});

// src/components/Select/SelectScrollUpButton.tsx
import { forwardRef as forwardRef73 } from "react";
import * as SelectPrimitive2 from "@radix-ui/react-select";
import { ChevronUpIcon as ChevronUpIcon2 } from "lucide-react";
import { jsx as jsx102 } from "react/jsx-runtime";
var SelectScrollUpButton = forwardRef73(function SelectScrollUpButton2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx102(
    SelectPrimitive2.ScrollUpButton,
    {
      className: cn("flex cursor-default items-center justify-center py-1", className),
      ref,
      ...props,
      children: /* @__PURE__ */ jsx102(ChevronUpIcon2, {})
    }
  );
});

// src/components/Select/SelectContent.tsx
import { jsx as jsx103, jsxs as jsxs27 } from "react/jsx-runtime";
var SelectContent = forwardRef74(function SelectContent2({ children, className, position = "popper", ...props }, ref) {
  return /* @__PURE__ */ jsx103(SelectPrimitive3.Portal, { children: /* @__PURE__ */ jsxs27(
    SelectPrimitive3.Content,
    {
      className: cn(
        "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      ),
      position,
      ref,
      ...props,
      children: [
        /* @__PURE__ */ jsx103(SelectScrollUpButton, {}),
        /* @__PURE__ */ jsx103(
          SelectPrimitive3.Viewport,
          {
            className: cn(
              "p-1",
              position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
            ),
            children
          }
        ),
        /* @__PURE__ */ jsx103(SelectScrollDownButton, {})
      ]
    }
  ) });
});

// src/components/Select/SelectItem.tsx
import { forwardRef as forwardRef75 } from "react";
import * as SelectPrimitive4 from "@radix-ui/react-select";
import { CheckIcon as CheckIcon4 } from "lucide-react";
import { jsx as jsx104, jsxs as jsxs28 } from "react/jsx-runtime";
var SelectItem = forwardRef75(function SelectItem2({ children, className, ...props }, ref) {
  return /* @__PURE__ */ jsxs28(
    SelectPrimitive4.Item,
    {
      className: cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      ),
      ref,
      ...props,
      children: [
        /* @__PURE__ */ jsx104("span", { className: "absolute right-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx104(SelectPrimitive4.ItemIndicator, { children: /* @__PURE__ */ jsx104(CheckIcon4, { className: "h-4 w-4" }) }) }),
        /* @__PURE__ */ jsx104(SelectPrimitive4.ItemText, { children })
      ]
    }
  );
});

// src/components/Select/SelectLabel.tsx
import { forwardRef as forwardRef76 } from "react";
import * as SelectPrimitive5 from "@radix-ui/react-select";
import { jsx as jsx105 } from "react/jsx-runtime";
var SelectLabel = forwardRef76(function SelectLabel2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx105(SelectPrimitive5.Label, { className: cn("px-2 py-1.5 text-sm font-semibold", className), ref, ...props });
});

// src/components/Select/SelectSeparator.tsx
import { forwardRef as forwardRef77 } from "react";
import * as SelectPrimitive6 from "@radix-ui/react-select";
import { jsx as jsx106 } from "react/jsx-runtime";
var SelectSeparator = forwardRef77(function SelectSeparator2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx106(SelectPrimitive6.Separator, { className: cn("-mx-1 my-1 h-px bg-muted", className), ref, ...props });
});

// src/components/Select/SelectTrigger.tsx
import { forwardRef as forwardRef78 } from "react";
import * as SelectPrimitive7 from "@radix-ui/react-select";
import { jsx as jsx107 } from "react/jsx-runtime";
var SelectTrigger = forwardRef78(function SelectTrigger2({ children, className, ...props }, ref) {
  return /* @__PURE__ */ jsx107(SelectPrimitive7.Trigger, { asChild: true, className, ref, ...props, children: /* @__PURE__ */ jsx107(DropdownButton, { children }) });
});

// src/components/Select/Select.tsx
var Select = Object.assign(SelectPrimitive8.Root.bind(null), {
  Content: SelectContent,
  Group: SelectPrimitive8.Group,
  Item: SelectItem,
  Label: SelectLabel,
  ScrollDownButton: SelectScrollDownButton,
  ScrollUpButton: SelectScrollUpButton,
  Separator: SelectSeparator,
  Trigger: SelectTrigger,
  Value: SelectPrimitive8.Value
});

// src/components/Form/NumberField/NumberFieldSelect.tsx
import { jsx as jsx108, jsxs as jsxs29 } from "react/jsx-runtime";
var NumberFieldSelect = ({
  description,
  disableAutoPrefix,
  disabled,
  error,
  label,
  name,
  options,
  readOnly,
  setValue,
  value
}) => {
  return /* @__PURE__ */ jsxs29(FieldGroup, { name, children: [
    /* @__PURE__ */ jsxs29(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx108(Label3, { children: label }),
      /* @__PURE__ */ jsx108(FieldGroup.Description, { description })
    ] }),
    /* @__PURE__ */ jsxs29(Select, { name, value: value?.toString() ?? "", onValueChange: (value2) => setValue(parseFloat(value2)), children: [
      /* @__PURE__ */ jsx108(Select.Trigger, { "data-testid": `${name}-select-trigger`, disabled: disabled || readOnly, children: /* @__PURE__ */ jsx108(Select.Value, {}) }),
      /* @__PURE__ */ jsx108(Select.Content, { "data-testid": `${name}-select-content`, children: Object.keys(options).map((option) => {
        const text = (disableAutoPrefix ? "" : `${option} - `) + options[option];
        return /* @__PURE__ */ jsx108(Select.Item, { "data-testid": `${name}-select-item-${option}`, value: option, children: text }, option);
      }) })
    ] }),
    /* @__PURE__ */ jsx108(FieldGroup.Error, { error })
  ] });
};

// src/components/Slider/Slider.tsx
import { forwardRef as forwardRef79 } from "react";
import { Range, Root as Root13, Thumb, Track } from "@radix-ui/react-slider";
import { jsx as jsx109, jsxs as jsxs30 } from "react/jsx-runtime";
var Slider = forwardRef79(
  function Slider2({ className, disabled, ...props }, ref) {
    return /* @__PURE__ */ jsxs30(
      Root13,
      {
        className: cn("relative flex w-full touch-none select-none items-center py-1.5", className),
        disabled,
        ref,
        ...props,
        children: [
          /* @__PURE__ */ jsx109(
            Track,
            {
              "aria-disabled": disabled,
              className: "relative h-1.5 w-full grow overflow-hidden rounded-full bg-primary opacity-15 aria-disabled:cursor-not-allowed aria-disabled:opacity-10",
              "data-testid": "slider-track",
              children: /* @__PURE__ */ jsx109(Range, { className: "absolute h-full bg-primary" })
            }
          ),
          /* @__PURE__ */ jsx109(
            Thumb,
            {
              "aria-disabled": disabled,
              className: "block h-4 w-4 rounded-full border border-slate-500 bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring aria-disabled:cursor-not-allowed",
              "data-testid": "slider-thumb"
            }
          )
        ]
      }
    );
  }
);

// src/components/Form/NumberField/NumberFieldSlider.tsx
import { jsx as jsx110, jsxs as jsxs31 } from "react/jsx-runtime";
var NumberFieldSlider = ({
  description,
  disabled,
  error,
  label,
  max,
  min,
  name,
  readOnly,
  setValue,
  value
}) => {
  return /* @__PURE__ */ jsxs31(FieldGroup, { name, children: [
    /* @__PURE__ */ jsxs31(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx110(Label3, { children: label }),
      /* @__PURE__ */ jsx110(FieldGroup.Description, { description })
    ] }),
    /* @__PURE__ */ jsxs31(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx110(
        Slider,
        {
          disabled: disabled || readOnly,
          max,
          min,
          name,
          value: [value ?? 0],
          onValueChange: ([value2]) => setValue(value2)
        }
      ),
      /* @__PURE__ */ jsx110("span", { className: "flex h-full w-8 items-center justify-center text-sm text-muted-foreground", children: value ?? "NA" })
    ] }),
    /* @__PURE__ */ jsx110(FieldGroup.Error, { error })
  ] });
};

// src/components/Form/NumberField/NumberField.tsx
import { jsx as jsx111 } from "react/jsx-runtime";
var NumberField = (props) => {
  return match2(props).with({ variant: "input" }, (props2) => /* @__PURE__ */ jsx111(NumberFieldInput, { ...props2 })).with({ variant: "slider" }, (props2) => /* @__PURE__ */ jsx111(NumberFieldSlider, { ...props2 })).with({ variant: "radio" }, (props2) => /* @__PURE__ */ jsx111(NumberFieldRadio, { ...props2 })).with({ variant: "select" }, (props2) => /* @__PURE__ */ jsx111(NumberFieldSelect, { ...props2 })).exhaustive();
};

// src/components/Form/NumberRecordField.tsx
import { jsx as jsx112, jsxs as jsxs32 } from "react/jsx-runtime";
var NumberRecordField = ({
  disabled,
  error: recordError,
  items,
  label,
  options,
  readOnly,
  setError: setRecordError,
  setValue: setRecordValue,
  value: recordValue
}) => {
  useEffect3(() => {
    setRecordValue({});
  }, [options]);
  if (!recordValue) {
    return null;
  }
  return /* @__PURE__ */ jsxs32("div", { className: "space-y-4", children: [
    /* @__PURE__ */ jsx112(Heading, { className: "font-medium", variant: "h5", children: label }),
    /* @__PURE__ */ jsx112("div", { className: "space-y-6", children: Object.keys(items).map((name) => {
      const item = items[name];
      return /* @__PURE__ */ jsx112(
        NumberField,
        {
          error: recordError?.[name],
          kind: "number",
          name,
          options,
          readOnly: disabled || readOnly,
          setError: (error) => setRecordError({ ...recordError, [name]: error }),
          setValue: (value) => setRecordValue({ ...recordValue, [name]: value }),
          value: recordValue?.[name],
          variant: "radio",
          ...item
        },
        name
      );
    }) })
  ] });
};

// src/components/Form/RecordArrayField.tsx
import { memo, useEffect as useEffect7 } from "react";
import { MinusCircleIcon, PlusCircleIcon } from "lucide-react";

// src/components/Form/BooleanField/BooleanField.tsx
import { match as match4 } from "ts-pattern";

// src/components/Form/BooleanField/BooleanFieldCheckbox.tsx
import { jsx as jsx113, jsxs as jsxs33 } from "react/jsx-runtime";
var BooleanFieldCheckbox = ({
  disabled,
  error,
  label,
  name,
  readOnly,
  setValue,
  value
}) => {
  return /* @__PURE__ */ jsxs33(FieldGroup, { name, children: [
    /* @__PURE__ */ jsxs33(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx113(
        Checkbox,
        {
          checked: value,
          disabled: disabled || readOnly,
          id: name,
          name,
          onCheckedChange: (value2) => {
            if (typeof value2 === "boolean") {
              setValue(value2);
            }
          }
        }
      ),
      /* @__PURE__ */ jsx113(Label3, { htmlFor: name, children: label })
    ] }),
    /* @__PURE__ */ jsx113(FieldGroup.Error, { error })
  ] });
};

// src/components/Form/BooleanField/BooleanFieldRadio.tsx
import { useCallback as useCallback2 } from "react";
import { match as match3 } from "ts-pattern";
import { jsx as jsx114, jsxs as jsxs34 } from "react/jsx-runtime";
var stringifyBoolean = (value) => match3(value).with(void 0, () => "").with(true, () => "true").with(false, () => "false").exhaustive();
var BooleanFieldRadio = ({
  disabled,
  error,
  label,
  name,
  options,
  readOnly,
  setValue,
  value
}) => {
  const { t } = useTranslation("libui");
  const handleValueChange = useCallback2(
    (value2) => {
      match3(value2).with("", () => setValue(void 0)).with("true", () => setValue(true)).with("false", () => setValue(false)).otherwise((value3) => console.error(`Unexpected value for boolean field '${name}': ${value3}`));
    },
    [match3, setValue]
  );
  return /* @__PURE__ */ jsxs34(FieldGroup, { name, children: [
    /* @__PURE__ */ jsx114(Label3, { children: label }),
    /* @__PURE__ */ jsxs34(
      RadioGroup4,
      {
        disabled: disabled || readOnly,
        name,
        value: stringifyBoolean(value),
        onValueChange: handleValueChange,
        children: [
          /* @__PURE__ */ jsxs34(FieldGroup.Row, { children: [
            /* @__PURE__ */ jsx114(RadioGroup4.Item, { id: `${name}-true`, value: "true" }),
            /* @__PURE__ */ jsx114(
              Label3,
              {
                "aria-disabled": disabled || readOnly,
                className: "font-normal text-muted-foreground",
                htmlFor: `${name}-true`,
                children: options?.true ?? t("form.radioLabels.true")
              }
            )
          ] }),
          /* @__PURE__ */ jsxs34(FieldGroup.Row, { children: [
            /* @__PURE__ */ jsx114(RadioGroup4.Item, { id: `${name}-false`, value: "false" }),
            /* @__PURE__ */ jsx114(
              Label3,
              {
                "aria-disabled": disabled || readOnly,
                className: "font-normal text-muted-foreground",
                htmlFor: `${name}-false`,
                children: options?.false ?? t("form.radioLabels.false")
              }
            )
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsx114(FieldGroup.Error, { error })
  ] });
};

// src/components/Form/BooleanField/BooleanField.tsx
import { jsx as jsx115 } from "react/jsx-runtime";
var BooleanField = (props) => {
  return match4(props).with({ variant: "radio" }, (props2) => /* @__PURE__ */ jsx115(BooleanFieldRadio, { ...props2 })).with({ variant: "checkbox" }, (props2) => /* @__PURE__ */ jsx115(BooleanFieldCheckbox, { ...props2 })).exhaustive();
};

// src/components/Form/DateField/DateField.tsx
import { useEffect as useEffect4, useState as useState5 } from "react";
import { toBasicISOString as toBasicISOString2 } from "@douglasneuroinformatics/libjs";
import { jsx as jsx116, jsxs as jsxs35 } from "react/jsx-runtime";
var isValidDateString = (s) => /^(\d{4})-((0[1-9])|(1[0-2]))-((0[1-9])|([12])[0-9]|3[01])$/.test(s);
var DateField = ({ disabled, error, label, name, readOnly, setValue, value }) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState5(false);
  const [isInputFocused, setIsInputFocused] = useState5(false);
  const [inputValue, setInputValue] = useState5("");
  useEffect4(() => {
    const isSelecting = isDatePickerOpen || isInputFocused;
    if (isSelecting) {
      return;
    } else if (isValidDateString(inputValue)) {
      setValue(new Date(inputValue));
    } else {
      setInputValue("");
    }
  }, [isDatePickerOpen, isInputFocused]);
  useEffect4(() => {
    setInputValue(value ? toBasicISOString2(value) : "");
  }, [value]);
  return /* @__PURE__ */ jsxs35(FieldGroup, { name, children: [
    /* @__PURE__ */ jsx116(Label3, { htmlFor: name, children: label }),
    /* @__PURE__ */ jsxs35(Popover, { open: isDatePickerOpen, onOpenChange: setIsDatePickerOpen, children: [
      /* @__PURE__ */ jsx116(Popover.Trigger, { children: /* @__PURE__ */ jsx116(
        Input,
        {
          autoComplete: "off",
          "data-testid": "date-input",
          disabled: disabled || readOnly,
          name,
          placeholder: "YYYY-MM-DD",
          type: "text",
          value: inputValue,
          onBlur: () => setIsInputFocused(false),
          onChange: (event) => setInputValue(event.target.value),
          onFocus: () => setIsInputFocused(true)
        }
      ) }),
      /* @__PURE__ */ jsx116(Popover.Content, { asChild: true, align: "start", autofocus: false, className: "w-auto", children: /* @__PURE__ */ jsx116(
        DatePicker,
        {
          onSelection: (value2) => {
            setInputValue(toBasicISOString2(value2));
            setIsDatePickerOpen(false);
          }
        }
      ) })
    ] }),
    /* @__PURE__ */ jsx116(FieldGroup.Error, { error })
  ] });
};

// src/components/Form/SetField/SetField.tsx
import { useEffect as useEffect5 } from "react";
import { match as match5 } from "ts-pattern";

// src/components/Form/SetField/SetFieldListbox.tsx
import { jsx as jsx117, jsxs as jsxs36 } from "react/jsx-runtime";
var SetFieldListbox = ({
  description,
  disabled,
  error,
  label,
  name,
  onCheckedChange,
  options,
  readOnly,
  value
}) => {
  return /* @__PURE__ */ jsxs36(FieldGroup, { name, children: [
    /* @__PURE__ */ jsxs36(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx117(Label3, { children: label }),
      /* @__PURE__ */ jsx117(FieldGroup.Description, { description })
    ] }),
    /* @__PURE__ */ jsx117("div", { className: "grid gap-2", children: Object.keys(options).map((option) => /* @__PURE__ */ jsxs36(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx117(
        Checkbox,
        {
          checked: value?.has(option) ?? false,
          disabled: disabled || readOnly,
          id: `${name}-${option}`,
          onCheckedChange: (checked) => {
            onCheckedChange(option, !checked);
          }
        }
      ),
      /* @__PURE__ */ jsx117(Label3, { className: "font-normal text-muted-foreground", htmlFor: `${name}-${option}`, children: options[option] })
    ] }, option)) }),
    /* @__PURE__ */ jsx117(FieldGroup.Error, { error })
  ] });
};

// src/components/Form/SetField/SetFieldSelect.tsx
import { jsx as jsx118, jsxs as jsxs37 } from "react/jsx-runtime";
var SetFieldSelect = ({
  description,
  disabled,
  error,
  label,
  name,
  onCheckedChange,
  options,
  readOnly,
  value
}) => {
  return value ? /* @__PURE__ */ jsxs37(FieldGroup, { name, children: [
    /* @__PURE__ */ jsxs37(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx118(Label3, { children: label }),
      /* @__PURE__ */ jsx118(FieldGroup.Description, { description })
    ] }),
    /* @__PURE__ */ jsxs37(DropdownMenu, { children: [
      /* @__PURE__ */ jsx118(DropdownMenu.Trigger, { asChild: true, className: "w-full", disabled: disabled || readOnly, children: /* @__PURE__ */ jsx118(DropdownButton, { children: value.size ? /* @__PURE__ */ jsx118("div", { className: "flex items-center gap-2", children: Array.from(value).map((option) => /* @__PURE__ */ jsx118(Badge, { className: "font-normal", variant: "outline", children: options[option] }, option)) }) : null }) }),
      /* @__PURE__ */ jsx118(DropdownMenu.Content, { widthFull: true, align: "start", children: Object.keys(options).map((option) => {
        const checked = value.has(option);
        return /* @__PURE__ */ jsx118(
          DropdownMenu.CheckboxItem,
          {
            checked,
            onSelect: (event) => {
              event.preventDefault();
              onCheckedChange(option, value.has(option));
            },
            children: options[option]
          },
          option
        );
      }) })
    ] }),
    /* @__PURE__ */ jsx118(FieldGroup.Error, { error })
  ] }) : null;
};

// src/components/Form/SetField/SetField.tsx
import { jsx as jsx119 } from "react/jsx-runtime";
var SetField = (props) => {
  useEffect5(() => {
    if (!props.value) {
      props.setValue(/* @__PURE__ */ new Set([]));
    }
  }, [props.value]);
  const handleCheckedChange = (option, isChecked) => {
    if (isChecked) {
      const updatedValue = new Set(props.value);
      updatedValue.delete(option);
      props.setValue(updatedValue);
    } else {
      const updatedValue = new Set(props.value);
      updatedValue.add(option);
      props.setValue(updatedValue);
    }
  };
  return match5(props).with({ variant: "select" }, (props2) => /* @__PURE__ */ jsx119(SetFieldSelect, { onCheckedChange: handleCheckedChange, ...props2 })).with({ variant: "listbox" }, (props2) => /* @__PURE__ */ jsx119(SetFieldListbox, { onCheckedChange: handleCheckedChange, ...props2 })).exhaustive();
};

// src/components/Form/StringField/StringField.tsx
import { match as match6 } from "ts-pattern";

// src/components/Form/StringField/StringFieldInput.tsx
import { jsx as jsx120, jsxs as jsxs38 } from "react/jsx-runtime";
var StringFieldInput = ({
  description,
  disabled,
  error,
  label,
  name,
  placeholder,
  readOnly,
  setValue,
  value
}) => {
  return /* @__PURE__ */ jsxs38(FieldGroup, { name, children: [
    /* @__PURE__ */ jsxs38(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx120(Label3, { htmlFor: name, children: label }),
      /* @__PURE__ */ jsx120(FieldGroup.Description, { description })
    ] }),
    /* @__PURE__ */ jsx120(
      Input,
      {
        disabled: disabled || readOnly,
        id: name,
        name,
        placeholder,
        type: "text",
        value: value ?? "",
        onChange: (event) => setValue(event.target.value)
      }
    ),
    /* @__PURE__ */ jsx120(FieldGroup.Error, { error })
  ] });
};

// src/components/Form/StringField/StringFieldPassword.tsx
import { useEffect as useEffect6, useState as useState6 } from "react";
import { motion as motion4 } from "framer-motion";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { jsx as jsx121, jsxs as jsxs39 } from "react/jsx-runtime";
var StringFieldPassword = ({
  calculateStrength,
  description,
  disabled,
  error,
  label,
  name,
  readOnly,
  setValue,
  value
}) => {
  const [strength, setStrength] = useState6(calculateStrength ? 0 : null);
  const [show, setShow] = useState6(false);
  useEffect6(() => {
    if (calculateStrength) {
      setStrength(value ? calculateStrength(value) : 0);
    }
  }, [value]);
  return /* @__PURE__ */ jsxs39(FieldGroup, { name, children: [
    /* @__PURE__ */ jsxs39(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx121(Label3, { htmlFor: name, children: label }),
      /* @__PURE__ */ jsx121(FieldGroup.Description, { description })
    ] }),
    /* @__PURE__ */ jsxs39(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx121(
        Input,
        {
          disabled: disabled || readOnly,
          id: name,
          name,
          type: show ? "text" : "password",
          value: value ?? "",
          onChange: (event) => setValue(event.target.value)
        }
      ),
      /* @__PURE__ */ jsxs39(
        "button",
        {
          className: "absolute right-0 flex h-full w-8 items-center justify-center text-muted-foreground",
          disabled: disabled || readOnly,
          tabIndex: -1,
          type: "button",
          onClick: () => setShow(!show),
          children: [
            /* @__PURE__ */ jsx121(EyeIcon, { className: cn("absolute transition-all", show ? "-rotate-90 scale-0" : "rotate-0 scale-100") }),
            /* @__PURE__ */ jsx121(EyeOffIcon, { className: cn("absolute transition-all", !show ? "rotate-90 scale-0" : "rotate-0 scale-100") })
          ]
        }
      )
    ] }),
    strength !== null && /* @__PURE__ */ jsx121(
      motion4.div,
      {
        animate: { width: `${Math.max(strength, value?.length ? 1 : 0) * 25}%` },
        className: "h-1 w-full overflow-hidden rounded",
        initial: { width: "0%" },
        transition: { duration: 0.5 },
        children: /* @__PURE__ */ jsx121(
          "div",
          {
            className: cn(
              "h-full w-full bg-destructive transition-colors duration-500",
              strength === 2 && "bg-yellow-500 dark:bg-yellow-700",
              strength === 3 && "bg-sky-500 dark:bg-sky-700",
              strength === 4 && "bg-green-500 dark:bg-green-700"
            )
          }
        )
      }
    ),
    /* @__PURE__ */ jsx121(FieldGroup.Error, { error })
  ] });
};

// src/components/Form/BaseRadioField.tsx
import { cva as cva4 } from "class-variance-authority";
import { jsx as jsx122, jsxs as jsxs40 } from "react/jsx-runtime";
var baseRadioFieldVariants = cva4("flex", {
  defaultVariants: {
    orientation: "vertical"
  },
  variants: {
    orientation: {
      horizontal: "flex-col @3xl:flex-row @3xl:items-center @3xl:justify-between",
      vertical: "flex-col"
    }
  }
});
var BaseRadioField = ({
  description,
  disabled,
  error,
  label,
  name,
  options,
  orientation = "vertical",
  readOnly,
  setValue,
  value
}) => {
  const optionsCount = Object.keys(options).length;
  return /* @__PURE__ */ jsxs40(FieldGroup, { name, children: [
    /* @__PURE__ */ jsxs40(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx122(Label3, { children: label }),
      /* @__PURE__ */ jsx122(FieldGroup.Description, { description })
    ] }),
    /* @__PURE__ */ jsx122(
      RadioGroup4,
      {
        className: baseRadioFieldVariants({ orientation: optionsCount > 5 ? "vertical" : orientation }),
        name,
        value: value ?? "",
        onValueChange: (value2) => setValue(value2),
        children: Object.keys(options).map((option) => /* @__PURE__ */ jsxs40("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx122(RadioGroup4.Item, { disabled: disabled || readOnly, id: `${name}-${option}`, value: option }),
          /* @__PURE__ */ jsx122(
            Label3,
            {
              "aria-disabled": disabled || readOnly,
              className: "font-normal text-muted-foreground",
              htmlFor: `${name}-${option}`,
              children: options[option]
            }
          )
        ] }, option))
      }
    ),
    /* @__PURE__ */ jsx122(FieldGroup.Error, { error })
  ] });
};

// src/components/Form/StringField/StringFieldRadio.tsx
import { jsx as jsx123 } from "react/jsx-runtime";
var StringFieldRadio = (props) => {
  return /* @__PURE__ */ jsx123(BaseRadioField, { ...props });
};

// src/components/Form/StringField/StringFieldSelect.tsx
import { jsx as jsx124, jsxs as jsxs41 } from "react/jsx-runtime";
var StringFieldSelect = ({
  description,
  disabled,
  error,
  label,
  name,
  options,
  readOnly,
  setValue,
  value
}) => {
  return /* @__PURE__ */ jsxs41(FieldGroup, { name, children: [
    /* @__PURE__ */ jsxs41(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx124(Label3, { children: label }),
      /* @__PURE__ */ jsx124(FieldGroup.Description, { description })
    ] }),
    /* @__PURE__ */ jsxs41(Select, { name, value: value ?? "", onValueChange: (value2) => setValue(value2), children: [
      /* @__PURE__ */ jsx124(Select.Trigger, { "data-testid": `${name}-select-trigger`, disabled: disabled || readOnly, children: /* @__PURE__ */ jsx124(Select.Value, {}) }),
      /* @__PURE__ */ jsx124(Select.Content, { "data-testid": `${name}-select-content`, children: Object.keys(options).map((option) => /* @__PURE__ */ jsx124(Select.Item, { "data-testid": `${name}-select-item-${option}`, value: option, children: options[option] }, option)) })
    ] }),
    /* @__PURE__ */ jsx124(FieldGroup.Error, { error })
  ] });
};

// src/components/TextArea/TextArea.tsx
import * as React32 from "react";
import { jsx as jsx125 } from "react/jsx-runtime";
var TextArea = React32.forwardRef(function TextArea2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx125(
    "textarea",
    {
      autoComplete: "off",
      className: cn(
        "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground placeholder:opacity-80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      "data-testid": "text-area",
      ref,
      ...props
    }
  );
});

// src/components/Form/StringField/StringFieldTextArea.tsx
import { jsx as jsx126, jsxs as jsxs42 } from "react/jsx-runtime";
var StringFieldTextArea = ({
  description,
  disabled,
  error,
  label,
  name,
  placeholder,
  readOnly,
  setValue,
  value
}) => {
  return /* @__PURE__ */ jsxs42(FieldGroup, { name, children: [
    /* @__PURE__ */ jsxs42(FieldGroup.Row, { children: [
      /* @__PURE__ */ jsx126(Label3, { htmlFor: name, children: label }),
      /* @__PURE__ */ jsx126(FieldGroup.Description, { description })
    ] }),
    /* @__PURE__ */ jsx126(
      TextArea,
      {
        disabled: disabled || readOnly,
        id: name,
        name,
        placeholder,
        rows: 5,
        value: value ?? "",
        onChange: (event) => setValue(event.target.value)
      }
    ),
    /* @__PURE__ */ jsx126(FieldGroup.Error, { error })
  ] });
};

// src/components/Form/StringField/StringField.tsx
import { jsx as jsx127 } from "react/jsx-runtime";
var StringField = (props) => {
  return match6(props).with({ variant: "textarea" }, (props2) => /* @__PURE__ */ jsx127(StringFieldTextArea, { ...props2 })).with({ variant: "password" }, (props2) => /* @__PURE__ */ jsx127(StringFieldPassword, { ...props2 })).with({ variant: "input" }, (props2) => /* @__PURE__ */ jsx127(StringFieldInput, { ...props2 })).with({ variant: "select" }, (props2) => /* @__PURE__ */ jsx127(StringFieldSelect, { ...props2 })).with({ variant: "radio" }, (props2) => /* @__PURE__ */ jsx127(StringFieldRadio, { ...props2 })).exhaustive();
};

// src/components/Form/ScalarField.tsx
import { jsx as jsx128 } from "react/jsx-runtime";
var ScalarField = ({ field, ...props }) => {
  switch (field.kind) {
    case "string":
      return /* @__PURE__ */ jsx128(StringField, { ...field, ...props });
    case "number":
      return /* @__PURE__ */ jsx128(NumberField, { ...field, ...props });
    case "date":
      return /* @__PURE__ */ jsx128(DateField, { ...field, ...props });
    case "boolean":
      return /* @__PURE__ */ jsx128(BooleanField, { ...field, ...props });
    case "set":
      return /* @__PURE__ */ jsx128(SetField, { ...field, ...props });
    default:
      throw new Error(`Unexpected value for kind: ${Reflect.get(field, "kind")}`);
  }
};

// src/components/Form/RecordArrayField.tsx
import { jsx as jsx129, jsxs as jsxs43 } from "react/jsx-runtime";
var RecordArrayField = memo(function RecordArrayField2({
  disabled,
  error: arrayError,
  fieldset,
  label,
  readOnly,
  setError: setArrayError,
  setValue: setArrayValue,
  value: arrayValue
}) {
  const { t } = useTranslation("libui");
  const createNewRecord = () => Object.fromEntries(Object.keys(fieldset).map((fieldName) => [fieldName, void 0]));
  useEffect7(() => {
    setArrayValue([createNewRecord()]);
  }, [fieldset]);
  if (!arrayValue) {
    return null;
  }
  const appendField = () => {
    setArrayValue([...arrayValue, createNewRecord()]);
  };
  const removeField = () => {
    if (arrayValue.length > 1) {
      setArrayValue(arrayValue.slice(0, arrayValue.length - 1));
    }
  };
  return /* @__PURE__ */ jsxs43("div", { className: "space-y-4", children: [
    /* @__PURE__ */ jsx129(Heading, { className: "font-medium", variant: "h5", children: label }),
    /* @__PURE__ */ jsx129("div", { className: "space-y-6", children: arrayValue.map((fields, i) => /* @__PURE__ */ jsxs43("div", { className: "space-y-4", children: [
      /* @__PURE__ */ jsx129(Label3, { className: "font-semibold italic", children: label + " " + (i + 1) }),
      Object.keys(fields).map((name) => {
        const field = fieldset[name];
        const fieldProps = field?.kind === "dynamic" ? field.render.call(void 0, fields) : field;
        if (!fieldProps) {
          return null;
        }
        return /* @__PURE__ */ jsx129(
          ScalarField,
          {
            error: arrayError?.[i]?.[name],
            field: {
              ...fieldProps,
              disabled: disabled || fieldProps.disabled
            },
            name,
            readOnly: disabled || readOnly,
            setError: (error) => {
              const newArrayError = arrayError ? [...arrayError] : [];
              if (!newArrayError[i]) {
                newArrayError[i] = {};
              }
              newArrayError[i][name] = error;
              setArrayError(newArrayError);
            },
            setValue: (value) => {
              const newArrayValue = [...arrayValue];
              newArrayValue[i][name] = value;
              setArrayValue(newArrayValue);
            },
            value: arrayValue?.[i]?.[name]
          },
          name
        );
      })
    ] }, i)) }),
    /* @__PURE__ */ jsxs43("div", { className: "flex gap-3", children: [
      /* @__PURE__ */ jsxs43(Button, { disabled: disabled || readOnly, type: "button", variant: "outline", onClick: appendField, children: [
        t("form.append"),
        /* @__PURE__ */ jsx129(PlusCircleIcon, { className: "ml-2" })
      ] }),
      /* @__PURE__ */ jsxs43(Button, { disabled: disabled || readOnly, type: "button", variant: "outline", onClick: removeField, children: [
        t("form.remove"),
        /* @__PURE__ */ jsx129(MinusCircleIcon, { className: "ml-2" })
      ] })
    ] })
  ] });
});

// src/components/Form/StaticField.tsx
import { jsx as jsx130 } from "react/jsx-runtime";
var StaticField = ({
  errors,
  field,
  name,
  readOnly,
  setErrors,
  setValues,
  values
}) => {
  const setError = useCallback3(
    (error) => {
      return setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
    },
    [setErrors]
  );
  const setValue = useCallback3(
    (value) => {
      return setValues((prevValues) => ({ ...prevValues, [name]: value }));
    },
    [setValues]
  );
  return match7(field).with({ kind: "record-array" }, (field2) => /* @__PURE__ */ jsx130(
    RecordArrayField,
    {
      ...field2,
      error: errors[name],
      name,
      readOnly,
      setError,
      setValue,
      value: values[name]
    }
  )).with({ kind: "number-record" }, (field2) => /* @__PURE__ */ jsx130(
    NumberRecordField,
    {
      ...field2,
      error: errors[name],
      name,
      readOnly,
      setError,
      setValue,
      value: values[name]
    }
  )).otherwise((field2) => /* @__PURE__ */ jsx130(
    ScalarField,
    {
      error: errors[name],
      field: field2,
      name,
      readOnly,
      setError,
      setValue,
      value: values[name]
    }
  ));
};

// src/components/Form/DynamicField.tsx
import { jsx as jsx131 } from "react/jsx-runtime";
var DynamicField = ({
  field,
  name,
  readOnly,
  setValues,
  values,
  ...props
}) => {
  const [dependentValues, setDependentValues] = useState7(pick(values, field.deps));
  const staticField = useMemo2(() => {
    return field.render.call(void 0, values);
  }, [dependentValues, field.render]);
  useEffect8(() => {
    if (!staticField) {
      setValues((prevValues) => ({ ...prevValues, [name]: void 0 }));
    }
  }, [staticField]);
  useEffect8(() => {
    for (const key of field.deps) {
      if (dependentValues[key] !== values[key]) {
        setDependentValues(pick(values, field.deps));
        break;
      }
    }
  }, [field.deps, values]);
  if (!staticField) {
    return null;
  }
  return /* @__PURE__ */ jsx131(StaticField, { ...props, field: staticField, name, readOnly, setValues, values });
};

// src/components/Form/FieldsComponent.tsx
import { createElement } from "react";
var FieldsComponent = ({ fields, ...props }) => {
  return Object.keys(fields).map((name) => {
    const field = fields[name];
    if (field.kind === "dynamic") {
      return /* @__PURE__ */ createElement(DynamicField, { ...props, field, key: name, name });
    }
    return /* @__PURE__ */ createElement(StaticField, { ...props, field, key: name, name });
  });
};

// src/components/Form/utils.ts
function getInitialValues(values) {
  const initialValues = {};
  for (const key in values) {
    const value = values[key];
    if (value === null || value === void 0) {
      continue;
    } else if (Array.isArray(value)) {
      initialValues[key] = value.map(getInitialValues);
    } else {
      initialValues[key] = value;
    }
  }
  return initialValues;
}

// src/components/Form/Form.tsx
import { jsx as jsx132, jsxs as jsxs44 } from "react/jsx-runtime";
var Form = ({
  className,
  content,
  fieldsFooter,
  id,
  initialValues,
  onError,
  onSubmit,
  preventResetValuesOnReset,
  readOnly,
  resetBtn,
  revalidateOnBlur,
  submitBtnLabel,
  validationSchema,
  ...props
}) => {
  const { resolvedLanguage, t } = useTranslation("libui");
  const [rootErrors, setRootErrors] = useState8([]);
  const [errors, setErrors] = useState8({});
  const [values, setValues] = useState8(
    initialValues ? getInitialValues(initialValues) : {}
  );
  const handleError = (error) => {
    const fieldErrors = {};
    for (const issue of error.issues) {
      if (issue.path.length > 0) {
        const current = get(fieldErrors, issue.path);
        if (current) {
          current.push(issue.message);
        } else {
          set(fieldErrors, issue.path, [issue.message]);
        }
      } else {
        setRootErrors((prevErrors) => [...prevErrors, issue.message]);
      }
    }
    setErrors(fieldErrors);
    if (onError) {
      onError(error);
    }
  };
  const reset = () => {
    setRootErrors([]);
    setErrors({});
    if (!preventResetValuesOnReset) {
      setValues({});
    }
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    const result = await validationSchema.safeParseAsync(values);
    if (result.success) {
      reset();
      await onSubmit(result.data);
    } else {
      console.error(result.error.issues);
      handleError(result.error);
    }
  };
  const isGrouped = Array.isArray(content);
  const revalidate = () => {
    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) {
      validationSchema.safeParseAsync(values).then((result) => {
        if (!result.success) {
          handleError(result.error);
        }
      }).catch(console.error);
    }
  };
  useEffect9(() => {
    revalidate();
  }, [resolvedLanguage]);
  return /* @__PURE__ */ jsxs44(
    "form",
    {
      autoComplete: "off",
      className: twMerge("w-full", isGrouped ? "space-y-8 divide-y" : "space-y-6", className),
      id,
      onBlur: revalidateOnBlur ? revalidate : void 0,
      onSubmit: (event) => void handleSubmit(event),
      ...props,
      children: [
        isGrouped ? content.map((fieldGroup, i) => {
          return /* @__PURE__ */ jsxs44("div", { className: "flex flex-col space-y-6 [&:not(:first-child)]:pt-8", children: [
            /* @__PURE__ */ jsxs44("div", { className: "space-y-1", children: [
              fieldGroup.title && /* @__PURE__ */ jsx132(Heading, { className: "text-base", variant: "h4", children: fieldGroup.title }),
              fieldGroup.description && /* @__PURE__ */ jsx132("p", { className: "text-sm italic leading-tight text-muted-foreground", children: fieldGroup.description })
            ] }),
            /* @__PURE__ */ jsx132(
              FieldsComponent,
              {
                errors,
                fields: fieldGroup.fields,
                readOnly,
                setErrors,
                setValues,
                values
              }
            )
          ] }, i);
        }) : /* @__PURE__ */ jsx132(
          FieldsComponent,
          {
            errors,
            fields: content,
            readOnly,
            setErrors,
            setValues,
            values
          }
        ),
        fieldsFooter,
        /* @__PURE__ */ jsxs44("div", { className: "flex w-full gap-3", children: [
          /* @__PURE__ */ jsx132(Button, { "aria-label": "Submit", className: "block w-full", disabled: readOnly, type: "submit", variant: "primary", children: submitBtnLabel ?? t("form.submit") }),
          resetBtn && /* @__PURE__ */ jsx132(
            Button,
            {
              "aria-label": "Reset",
              className: "block w-full",
              disabled: readOnly,
              type: "button",
              variant: "secondary",
              onClick: reset,
              children: t("form.reset")
            }
          )
        ] }),
        Boolean(rootErrors.length) && /* @__PURE__ */ jsx132(ErrorMessage, { error: rootErrors })
      ]
    }
  );
};

// src/components/HoverCard/HoverCard.tsx
import { Root as Root14, Trigger as Trigger8 } from "@radix-ui/react-hover-card";

// src/components/HoverCard/HoverCardContent.tsx
import { forwardRef as forwardRef81 } from "react";
import { Content as Content9 } from "@radix-ui/react-hover-card";
import { jsx as jsx133 } from "react/jsx-runtime";
var HoverCardContent = forwardRef81(function HoverCardContent2({ align = "center", className, sideOffset = 4, ...props }, ref) {
  return /* @__PURE__ */ jsx133(
    Content9,
    {
      align,
      className: cn(
        "z-50 w-64 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      ),
      ref,
      sideOffset,
      ...props
    }
  );
});

// src/components/HoverCard/HoverCard.tsx
var HoverCard = Object.assign(Root14.bind(null), {
  Content: HoverCardContent,
  Trigger: Trigger8
});

// src/components/LanguageToggle/LanguageToggle.tsx
import { LanguagesIcon } from "lucide-react";
import { jsx as jsx134, jsxs as jsxs45 } from "react/jsx-runtime";
var LanguageToggle = ({
  align = "start",
  contentClassName,
  itemClassName,
  options = {},
  triggerClassName,
  variant = "outline"
}) => {
  const { changeLanguage } = useTranslation("libui");
  return /* @__PURE__ */ jsxs45(DropdownMenu, { children: [
    /* @__PURE__ */ jsx134(DropdownMenu.Trigger, { asChild: true, children: /* @__PURE__ */ jsx134(Button, { className: triggerClassName, size: "icon", variant, children: /* @__PURE__ */ jsx134(LanguagesIcon, {}) }) }),
    /* @__PURE__ */ jsx134(DropdownMenu.Content, { align, className: contentClassName, children: Object.keys(options).map((option) => /* @__PURE__ */ jsx134(
      DropdownMenu.Item,
      {
        className: itemClassName,
        onClick: () => void changeLanguage(option),
        children: options[option]
      },
      option
    )) })
  ] });
};

// src/components/LineGraph/LineGraph.tsx
import * as React37 from "react";
import { toBasicISOString as toBasicISOString3 } from "@douglasneuroinformatics/libjs";
import {
  CartesianGrid,
  ErrorBar,
  Label as Label6,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { jsx as jsx135, jsxs as jsxs46 } from "react/jsx-runtime";
import { createElement as createElement2 } from "react";
var strokeColors = {
  dark: "#cbd5e1",
  // slate-300
  light: "#475569"
  // slate-600
};
var tooltipStyles = {
  dark: {
    backgroundColor: "#0f172a",
    // slate-900
    borderColor: strokeColors.light,
    borderRadius: "2px"
  },
  light: {
    backgroundColor: "#f1f5f9",
    // slate-100
    borderColor: strokeColors.dark,
    borderRadius: "2px"
  }
};
function LineGraphComponent({
  data,
  lines,
  xAxis
}) {
  const { resolvedLanguage } = useTranslation("libui");
  const [theme] = useTheme();
  return /* @__PURE__ */ jsx135(ResponsiveContainer, { height: 400, width: "100%", children: /* @__PURE__ */ jsxs46(LineChart, { data: [...data], margin: { bottom: 5, left: 15, right: 15, top: 5 }, children: [
    /* @__PURE__ */ jsx135(CartesianGrid, { stroke: "#64748b", strokeDasharray: "5 5" }),
    /* @__PURE__ */ jsx135(
      XAxis,
      {
        axisLine: { stroke: "#64748b" },
        dataKey: xAxis?.key,
        domain: ["auto", "auto"],
        height: 50,
        interval: "preserveStartEnd",
        padding: { left: 20, right: 20 },
        stroke: strokeColors[theme],
        tickFormatter: (time) => toBasicISOString3(new Date(time)),
        tickLine: { stroke: "#64748b" },
        tickMargin: 8,
        tickSize: 8,
        type: "number",
        children: /* @__PURE__ */ jsx135(Label6, { fill: strokeColors[theme], offset: 0, position: "insideBottom", value: xAxis?.label })
      }
    ),
    /* @__PURE__ */ jsx135(
      YAxis,
      {
        axisLine: { stroke: "#64748b" },
        stroke: strokeColors[theme],
        tickLine: { stroke: "#64748b" },
        tickMargin: 5,
        tickSize: 8,
        width: 40
      }
    ),
    /* @__PURE__ */ jsx135(
      Tooltip,
      {
        contentStyle: tooltipStyles[theme],
        labelFormatter: (time) => {
          const date = new Date(time);
          return new Intl.DateTimeFormat(resolvedLanguage, {
            dateStyle: "full",
            timeStyle: "medium"
          }).format(date);
        },
        labelStyle: { color: strokeColors[theme], fontWeight: 500, whiteSpace: "pre-wrap" }
      }
    ),
    lines.map(({ err, name, stroke, type, val, ...props }) => /* @__PURE__ */ createElement2(
      Line,
      {
        ...props,
        dataKey: val,
        key: val,
        name,
        stroke: stroke ?? strokeColors[theme],
        type: type ?? "linear"
      },
      err && /* @__PURE__ */ jsx135(ErrorBar, { dataKey: err, stroke: "#64748b" })
    )),
    /* @__PURE__ */ jsx135(Legend, { wrapperStyle: { paddingLeft: 40, paddingTop: 10 } })
  ] }) });
}
var LineGraph = React37.memo(LineGraphComponent);

// src/components/ListboxDropdown/ListboxDropdown.tsx
import "react";
import { jsx as jsx136, jsxs as jsxs47 } from "react/jsx-runtime";
var ListboxDropdown = ({
  contentClassName,
  disabled,
  options,
  selected,
  setSelected,
  title,
  triggerClassName,
  widthFull
}) => {
  return /* @__PURE__ */ jsxs47(DropdownMenu, { children: [
    /* @__PURE__ */ jsx136(DropdownMenu.Trigger, { asChild: true, className: cn("w-full", triggerClassName), disabled, children: /* @__PURE__ */ jsx136(DropdownButton, { children: title }) }),
    /* @__PURE__ */ jsx136(DropdownMenu.Content, { align: "start", className: contentClassName, widthFull, children: options.map((option) => {
      const checked = Boolean(selected.find((selectedOption) => selectedOption.key === option.key));
      return /* @__PURE__ */ jsx136(
        DropdownMenu.CheckboxItem,
        {
          checked,
          className: "flex w-full items-center whitespace-nowrap bg-slate-50 p-2 text-sm hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700",
          "data-testid": "select-dropdown-option",
          onSelect: (event) => {
            event.preventDefault();
            if (checked) {
              setSelected((prevSelected) => {
                return prevSelected.filter((selectedOption) => selectedOption.key !== option.key);
              });
            } else {
              setSelected((prevSelected) => [...prevSelected, option]);
            }
          },
          children: option.label
        },
        option.key
      );
    }) })
  ] });
};

// src/components/MenuBar/MenuBar.tsx
import { Group as Group4, Menu, Portal as Portal10, RadioGroup as RadioGroup5, Sub as Sub3 } from "@radix-ui/react-menubar";

// src/components/MenuBar/MenuBarCheckboxItem.tsx
import { forwardRef as forwardRef82 } from "react";
import { CheckboxItem as CheckboxItem3, ItemIndicator as ItemIndicator6 } from "@radix-ui/react-menubar";
import { CheckIcon as CheckIcon5 } from "lucide-react";
import { jsx as jsx137, jsxs as jsxs48 } from "react/jsx-runtime";
var MenuBarCheckboxItem = forwardRef82(function MenuBarCheckboxItem2({ checked, children, className, ...props }, ref) {
  return /* @__PURE__ */ jsxs48(
    CheckboxItem3,
    {
      checked,
      className: cn(
        "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      ),
      ref,
      ...props,
      children: [
        /* @__PURE__ */ jsx137("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx137(ItemIndicator6, { children: /* @__PURE__ */ jsx137(CheckIcon5, { className: "h-4 w-4" }) }) }),
        children
      ]
    }
  );
});

// src/components/MenuBar/MenuBarContent.tsx
import { forwardRef as forwardRef83 } from "react";
import { Content as Content10, Portal as Portal9 } from "@radix-ui/react-menubar";
import { jsx as jsx138 } from "react/jsx-runtime";
var MenuBarContent = forwardRef83(function MenuBarContent2({ align = "start", alignOffset = -4, className, sideOffset = 8, ...props }, ref) {
  return /* @__PURE__ */ jsx138(Portal9, { children: /* @__PURE__ */ jsx138(
    Content10,
    {
      align,
      alignOffset,
      className: cn(
        "z-50 min-w-[12rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      ),
      ref,
      sideOffset,
      ...props
    }
  ) });
});

// src/components/MenuBar/MenuBarItem.tsx
import { forwardRef as forwardRef84 } from "react";
import { Item as Item6 } from "@radix-ui/react-menubar";
import { jsx as jsx139 } from "react/jsx-runtime";
var MenuBarItem = forwardRef84(function MenuBarItem2({ className, inset, ...props }, ref) {
  return /* @__PURE__ */ jsx139(
    Item6,
    {
      className: cn(
        "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        inset && "pl-8",
        className
      ),
      ref,
      ...props
    }
  );
});

// src/components/MenuBar/MenuBarLabel.tsx
import { forwardRef as forwardRef85 } from "react";
import { Label as Label7 } from "@radix-ui/react-menubar";
import { jsx as jsx140 } from "react/jsx-runtime";
var MenuBarLabel = forwardRef85(function MenuBarLabel2({ className, inset, ...props }, ref) {
  return /* @__PURE__ */ jsx140(Label7, { className: cn("px-2 py-1.5 text-sm font-semibold", inset && "pl-8", className), ref, ...props });
});

// src/components/MenuBar/MenuBarRadioItem.tsx
import { forwardRef as forwardRef86 } from "react";
import { ItemIndicator as ItemIndicator7, RadioItem as RadioItem3 } from "@radix-ui/react-menubar";
import { CircleIcon as CircleIcon4 } from "lucide-react";
import { jsx as jsx141, jsxs as jsxs49 } from "react/jsx-runtime";
var MenuBarRadioItem = forwardRef86(function MenuBarRadioItem2({ children, className, ...props }, ref) {
  return /* @__PURE__ */ jsxs49(
    RadioItem3,
    {
      className: cn(
        "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      ),
      ref,
      ...props,
      children: [
        /* @__PURE__ */ jsx141("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx141(ItemIndicator7, { children: /* @__PURE__ */ jsx141(CircleIcon4, { className: "fill-current", style: { height: 8, width: 8 } }) }) }),
        children
      ]
    }
  );
});

// src/components/MenuBar/MenuBarRoot.tsx
import { forwardRef as forwardRef87 } from "react";
import { Root as Root15 } from "@radix-ui/react-menubar";
import { jsx as jsx142 } from "react/jsx-runtime";
var MenuBarRoot = forwardRef87(
  function MenuBarRoot2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx142(
      Root15,
      {
        className: cn("flex h-9 items-center space-x-1 rounded-md border bg-background p-1 shadow-sm", className),
        ref,
        ...props
      }
    );
  }
);

// src/components/MenuBar/MenuBarSeparator.tsx
import { forwardRef as forwardRef88 } from "react";
import { Separator as Separator4 } from "@radix-ui/react-menubar";
import { jsx as jsx143 } from "react/jsx-runtime";
var MenuBarSeparator = forwardRef88(function MenuBarSeparator2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx143(Separator4, { className: cn("-mx-1 my-1 h-px bg-muted", className), ref, ...props });
});

// src/components/MenuBar/MenuBarShortcut.tsx
import "react";
import { jsx as jsx144 } from "react/jsx-runtime";
var MenuBarShortcut = ({ className, ...props }) => {
  return /* @__PURE__ */ jsx144("span", { className: cn("ml-auto text-xs tracking-widest text-muted-foreground", className), ...props });
};

// src/components/MenuBar/MenuBarSubContent.tsx
import { forwardRef as forwardRef89 } from "react";
import { SubContent as SubContent3 } from "@radix-ui/react-menubar";
import { jsx as jsx145 } from "react/jsx-runtime";
var MenuBarSubContent = forwardRef89(function MenuBarSubContent2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx145(
    SubContent3,
    {
      className: cn(
        "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      ),
      ref,
      ...props
    }
  );
});

// src/components/MenuBar/MenuBarSubTrigger.tsx
import { forwardRef as forwardRef90 } from "react";
import { SubTrigger as SubTrigger3 } from "@radix-ui/react-menubar";
import { ChevronRightIcon as ChevronRightIcon4 } from "lucide-react";
import { jsx as jsx146, jsxs as jsxs50 } from "react/jsx-runtime";
var MenuBarSubTrigger = forwardRef90(function MenuBarSubTrigger2({ children, className, inset, ...props }, ref) {
  return /* @__PURE__ */ jsxs50(
    SubTrigger3,
    {
      className: cn(
        "flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
        inset && "pl-8",
        className
      ),
      ref,
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx146(ChevronRightIcon4, { className: "ml-auto h-4 w-4" })
      ]
    }
  );
});

// src/components/MenuBar/MenuBarTrigger.tsx
import { forwardRef as forwardRef91 } from "react";
import { Trigger as Trigger9 } from "@radix-ui/react-menubar";
import { jsx as jsx147 } from "react/jsx-runtime";
var MenuBarTrigger = forwardRef91(function MenuBarTrigger2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx147(
    Trigger9,
    {
      className: cn(
        "flex cursor-default select-none items-center rounded-sm px-3 py-1 text-sm font-medium outline-none focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
        className
      ),
      ref,
      ...props
    }
  );
});

// src/components/MenuBar/MenuBar.tsx
var MenuBar = Object.assign(MenuBarRoot, {
  CheckboxItem: MenuBarCheckboxItem,
  Content: MenuBarContent,
  Group: Group4,
  Item: MenuBarItem,
  Label: MenuBarLabel,
  Menu,
  Portal: Portal10,
  RadioGroup: RadioGroup5,
  RadioItem: MenuBarRadioItem,
  Separator: MenuBarSeparator,
  Shortcut: MenuBarShortcut,
  Sub: Sub3,
  SubContent: MenuBarSubContent,
  SubTrigger: MenuBarSubTrigger,
  Trigger: MenuBarTrigger
});

// src/components/NotificationHub/NotificationHub.tsx
import { AnimatePresence as AnimatePresence4, motion as motion5 } from "framer-motion";
import { XIcon as XIcon2 } from "lucide-react";

// src/components/NotificationHub/NotificationIcon.tsx
import { CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { jsx as jsx148 } from "react/jsx-runtime";
var NotificationIcon = ({ type }) => {
  switch (type) {
    case "error":
      return /* @__PURE__ */ jsx148(XCircleIcon, { "aria-hidden": "true", className: "h-6 w-6 text-red-500" });
    case "info":
      return /* @__PURE__ */ jsx148(InformationCircleIcon, { "aria-hidden": "true", className: "h-6 w-6 text-blue-500" });
    case "success":
      return /* @__PURE__ */ jsx148(CheckCircleIcon, { "aria-hidden": "true", className: "h-6 w-6 text-green-500" });
    case "warning":
      return /* @__PURE__ */ jsx148(ExclamationCircleIcon, { "aria-hidden": "true", className: "h-6 w-6 text-yellow-500" });
  }
};

// src/components/NotificationHub/NotificationHub.tsx
import { jsx as jsx149, jsxs as jsxs51 } from "react/jsx-runtime";
var NotificationHub = ({ timeout = 5e3 }) => {
  const { t } = useTranslation("libui");
  const { dismissNotification, notifications } = useNotificationsStore();
  return /* @__PURE__ */ jsx149("div", { className: "fixed bottom-0 z-50 w-full print:hidden", children: /* @__PURE__ */ jsx149(AnimatePresence4, { children: notifications.map((item) => /* @__PURE__ */ jsx149(
    motion5.div,
    {
      animate: { height: "auto", opacity: 1 },
      className: "relative max-w-sm",
      exit: { height: 0, opacity: 0 },
      initial: { height: 0, opacity: 0 },
      transition: { bounce: 0.1, type: "spring" },
      children: /* @__PURE__ */ jsx149("div", { className: "w-full p-2", children: /* @__PURE__ */ jsxs51(Card, { className: "w-full rounded-lg p-0", children: [
        /* @__PURE__ */ jsxs51("div", { className: "p-4", children: [
          /* @__PURE__ */ jsxs51("div", { className: "mb-2 flex items-center", children: [
            /* @__PURE__ */ jsx149(NotificationIcon, { type: item.type }),
            /* @__PURE__ */ jsx149("h5", { className: "ml-3 flex-grow font-medium text-slate-900 dark:text-slate-100", children: item.title ?? t(`notifications.types.${item.type}`) }),
            /* @__PURE__ */ jsx149(
              "button",
              {
                className: "inline-flex rounded-md text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500 dark:focus:ring-sky-600",
                type: "button",
                onClick: () => {
                  dismissNotification(item.id);
                },
                children: /* @__PURE__ */ jsx149(XIcon2, { "aria-hidden": "true", className: "h-5 w-5" })
              }
            )
          ] }),
          /* @__PURE__ */ jsx149("p", { className: "my-2 text-muted-foreground", children: item.message })
        ] }),
        /* @__PURE__ */ jsx149(
          motion5.div,
          {
            animate: { width: "100%" },
            className: "h-1 bg-slate-500",
            initial: { width: "0%" },
            transition: { duration: timeout / 1e3, ease: "linear" },
            onAnimationComplete: () => {
              dismissNotification(item.id);
            }
          }
        )
      ] }) })
    },
    item.id
  )) }) });
};

// src/components/Pagination/PaginationContent.tsx
import "react";
import { jsx as jsx150 } from "react/jsx-runtime";
var PaginationContent = ({ className, ...props }) => /* @__PURE__ */ jsx150("ul", { className: cn("flex flex-row items-center gap-1", className), ...props });

// src/components/Pagination/PaginationEllipsis.tsx
import "react";
import { MoreHorizontalIcon as MoreHorizontalIcon2 } from "lucide-react";
import { jsx as jsx151, jsxs as jsxs52 } from "react/jsx-runtime";
var PaginationEllipsis = ({ className, ...props }) => /* @__PURE__ */ jsxs52("span", { "aria-hidden": true, className: cn("flex h-9 w-9 items-center justify-center", className), ...props, children: [
  /* @__PURE__ */ jsx151(MoreHorizontalIcon2, { className: "h-4 w-4" }),
  /* @__PURE__ */ jsx151("span", { className: "sr-only", children: "More pages" })
] });

// src/components/Pagination/PaginationItem.tsx
import "react";
import { jsx as jsx152 } from "react/jsx-runtime";
var PaginationItem = ({ className, ...props }) => /* @__PURE__ */ jsx152("li", { className, ...props });

// src/components/Pagination/PaginationLink.tsx
import "react";
import { jsx as jsx153 } from "react/jsx-runtime";
var PaginationLink = ({ children, className, isActive, size = "icon", ...props }) => /* @__PURE__ */ jsx153(
  "a",
  {
    "aria-current": isActive ? "page" : void 0,
    className: cn(
      buttonVariants({
        size,
        variant: isActive ? "outline" : "ghost"
      }),
      className
    ),
    ...props,
    children
  }
);

// src/components/Pagination/PaginationNext.tsx
import "react";
import { ChevronRightIcon as ChevronRightIcon5 } from "lucide-react";
import { jsx as jsx154, jsxs as jsxs53 } from "react/jsx-runtime";
var PaginationNext = ({ className, ...props }) => {
  const { t } = useTranslation("libui");
  return /* @__PURE__ */ jsxs53(PaginationLink, { "aria-label": "Go to next page", className: cn("gap-1 pr-2.5", className), size: "md", ...props, children: [
    /* @__PURE__ */ jsx154("span", { children: t("pagination.next") }),
    /* @__PURE__ */ jsx154(ChevronRightIcon5, { className: "h-4 w-4" })
  ] });
};

// src/components/Pagination/PaginationPrevious.tsx
import { ChevronLeftIcon } from "lucide-react";
import { jsx as jsx155, jsxs as jsxs54 } from "react/jsx-runtime";
var PaginationPrevious = ({ className, ...props }) => {
  const { t } = useTranslation("libui");
  return /* @__PURE__ */ jsxs54(PaginationLink, { "aria-label": "Go to previous page", className: cn("gap-1 pl-2.5", className), size: "md", ...props, children: [
    /* @__PURE__ */ jsx155(ChevronLeftIcon, { className: "h-4 w-4" }),
    /* @__PURE__ */ jsx155("span", { children: t("pagination.previous") })
  ] });
};

// src/components/Pagination/PaginationRoot.tsx
import "react";
import { jsx as jsx156 } from "react/jsx-runtime";
var PaginationRoot = ({ className, ...props }) => /* @__PURE__ */ jsx156(
  "nav",
  {
    "aria-label": "pagination",
    className: cn("mx-auto flex w-full justify-center", className),
    role: "navigation",
    ...props
  }
);

// src/components/Pagination/Pagination.tsx
var Pagination = Object.assign(PaginationRoot, {
  Content: PaginationContent,
  Ellipsis: PaginationEllipsis,
  Item: PaginationItem,
  Link: PaginationLink,
  Next: PaginationNext,
  Previous: PaginationPrevious
});

// src/components/Progress/Progress.tsx
import { forwardRef as forwardRef92 } from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { jsx as jsx157 } from "react/jsx-runtime";
var Progress = forwardRef92(function Progress2({ className, value, ...props }, ref) {
  return /* @__PURE__ */ jsx157(
    ProgressPrimitive.Root,
    {
      className: cn("bg-primary/20 relative h-2 w-full overflow-hidden rounded-full", className),
      ref,
      ...props,
      children: /* @__PURE__ */ jsx157(
        ProgressPrimitive.Indicator,
        {
          className: "h-full w-full flex-1 bg-primary transition-all",
          style: { transform: `translateX(-${100 - (value ?? 0)}%)` }
        }
      )
    }
  );
});

// src/components/Resizable/Resizable.tsx
import "react";
import { Panel } from "react-resizable-panels";

// src/components/Resizable/ResizableHandle.tsx
import "react";
import { GripVertical } from "lucide-react";
import { PanelResizeHandle } from "react-resizable-panels";
import { jsx as jsx158 } from "react/jsx-runtime";
var ResizableHandle = ({ className, withHandle, ...props }) => /* @__PURE__ */ jsx158(
  PanelResizeHandle,
  {
    className: cn(
      "relative flex w-px items-center justify-center bg-border after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 data-[panel-group-direction=vertical]:h-px data-[panel-group-direction=vertical]:w-full data-[panel-group-direction=vertical]:after:left-0 data-[panel-group-direction=vertical]:after:h-1 data-[panel-group-direction=vertical]:after:w-full data-[panel-group-direction=vertical]:after:-translate-y-1/2 data-[panel-group-direction=vertical]:after:translate-x-0 [&[data-panel-group-direction=vertical]>div]:rotate-90",
      className
    ),
    ...props,
    children: withHandle && /* @__PURE__ */ jsx158("div", { className: "z-10 flex h-4 w-3 items-center justify-center rounded-sm border bg-border", children: /* @__PURE__ */ jsx158(GripVertical, { className: "h-2.5 w-2.5" }) })
  }
);

// src/components/Resizable/ResizablePanelGroup.tsx
import "react";
import { PanelGroup } from "react-resizable-panels";
import { jsx as jsx159 } from "react/jsx-runtime";
var ResizablePanelGroup = ({ className, ...props }) => /* @__PURE__ */ jsx159(
  PanelGroup,
  {
    className: cn("flex h-full w-full data-[panel-group-direction=vertical]:flex-col", className),
    ...props
  }
);

// src/components/Resizable/Resizable.tsx
import { Fragment, jsx as jsx160 } from "react/jsx-runtime";
var ResizableRoot = ({ children }) => /* @__PURE__ */ jsx160(Fragment, { children });
var Resizable = Object.assign(ResizableRoot, {
  Handle: ResizableHandle,
  Panel,
  PanelGroup: ResizablePanelGroup
});

// src/components/SearchBar/SearchBar.tsx
import { SearchIcon as SearchIcon2 } from "lucide-react";
import { jsx as jsx161, jsxs as jsxs55 } from "react/jsx-runtime";
var SearchBar = ({
  className,
  onClick,
  onValueChange,
  placeholder,
  readOnly,
  value,
  ...props
}) => {
  const { t } = useTranslation("libui");
  return /* @__PURE__ */ jsxs55("form", { className: cn("relative", className), ...props, children: [
    /* @__PURE__ */ jsx161(SearchIcon2, { className: "absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" }),
    /* @__PURE__ */ jsx161(
      Input,
      {
        className: "pl-8",
        placeholder: placeholder ?? t("searchBar.placeholder"),
        readOnly,
        type: "search",
        value,
        onChange: (event) => {
          onValueChange?.(event.target.value);
        },
        onClick: () => onClick?.()
      }
    )
  ] });
};

// src/components/Separator/Separator.tsx
import { forwardRef as forwardRef93 } from "react";
import * as SeparatorPrimitive from "@radix-ui/react-separator";
import { jsx as jsx162 } from "react/jsx-runtime";
var Separator5 = forwardRef93(function Separator6({ className, decorative = true, orientation = "horizontal", ...props }, ref) {
  return /* @__PURE__ */ jsx162(
    SeparatorPrimitive.Root,
    {
      className: cn(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className
      ),
      "data-testid": "separator",
      decorative,
      orientation,
      ref,
      ...props
    }
  );
});

// src/components/Sheet/Sheet.tsx
import { Close as Close3, Portal as Portal12, Root as Root18, Trigger as Trigger10 } from "@radix-ui/react-dialog";

// src/components/Sheet/SheetBody.tsx
import "react";
import { jsx as jsx163 } from "react/jsx-runtime";
var SheetBody = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx163("div", { className: cn("py-4", className), ...props, children });
};

// src/components/Sheet/SheetContent.tsx
import * as React50 from "react";
import { Close as Close2, Content as Content11, Overlay as Overlay3, Portal as Portal11 } from "@radix-ui/react-dialog";
import { cva as cva5 } from "class-variance-authority";
import { XIcon as XIcon3 } from "lucide-react";
import { jsx as jsx164, jsxs as jsxs56 } from "react/jsx-runtime";
var sheetVariants = cva5(
  "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    defaultVariants: {
      side: "right"
    },
    variants: {
      side: {
        bottom: "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right: "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top"
      }
    }
  }
);
var SheetContent = React50.forwardRef(function SheetContent2({ children, className, side = "right", ...props }, ref) {
  return /* @__PURE__ */ jsxs56(Portal11, { children: [
    /* @__PURE__ */ jsx164(Overlay3, {}),
    /* @__PURE__ */ jsxs56(Content11, { className: cn(sheetVariants({ side }), className), ref, ...props, children: [
      children,
      /* @__PURE__ */ jsxs56(Close2, { className: "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary", children: [
        /* @__PURE__ */ jsx164(XIcon3, { className: "h-4 w-4" }),
        /* @__PURE__ */ jsx164("span", { className: "sr-only", children: "Close" })
      ] })
    ] })
  ] });
});

// src/components/Sheet/SheetDescription.tsx
import { forwardRef as forwardRef95 } from "react";
import { Description as Description3 } from "@radix-ui/react-dialog";
import { jsx as jsx165 } from "react/jsx-runtime";
var SheetDescription = forwardRef95(function SheetDescription2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx165(Description3, { className: cn("text-sm text-muted-foreground", className), ref, ...props });
});

// src/components/Sheet/SheetFooter.tsx
import "react";
import { jsx as jsx166 } from "react/jsx-runtime";
var SheetFooter = ({ className, ...props }) => /* @__PURE__ */ jsx166("div", { className: cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className), ...props });

// src/components/Sheet/SheetHeader.tsx
import "react";
import { jsx as jsx167 } from "react/jsx-runtime";
var SheetHeader = ({ className, ...props }) => /* @__PURE__ */ jsx167("div", { className: cn("flex flex-col space-y-2 text-center sm:text-left", className), ...props });

// src/components/Sheet/SheetOverlay.tsx
import { forwardRef as forwardRef96 } from "react";
import { Overlay as Overlay4 } from "@radix-ui/react-dialog";
import { jsx as jsx168 } from "react/jsx-runtime";
var SheetOverlay = forwardRef96(function SheetOverlay2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx168(
    Overlay4,
    {
      className: cn(
        "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      ),
      ...props,
      ref
    }
  );
});

// src/components/Sheet/SheetTitle.tsx
import { forwardRef as forwardRef97 } from "react";
import { Title as Title3 } from "@radix-ui/react-dialog";
import { jsx as jsx169 } from "react/jsx-runtime";
var SheetTitle = forwardRef97(
  function SheetTitle2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx169(Title3, { className: cn("text-lg font-semibold text-foreground", className), ref, ...props });
  }
);

// src/components/Sheet/Sheet.tsx
var Sheet = Object.assign(Root18.bind(null), {
  Body: SheetBody,
  Close: Close3,
  Content: SheetContent,
  Description: SheetDescription,
  Footer: SheetFooter,
  Header: SheetHeader,
  Overlay: SheetOverlay,
  Portal: Portal12,
  Title: SheetTitle,
  Trigger: Trigger10
});

// src/components/Spinner/Spinner.tsx
import { jsx as jsx170 } from "react/jsx-runtime";
var Spinner = ({ className, ...props }) => {
  return /* @__PURE__ */ jsx170("div", { className: cn("flex h-full w-full items-center justify-center", className), ...props, children: /* @__PURE__ */ jsx170(
    "span",
    {
      className: "relative animate-spinner overflow-hidden text-slate-900 dark:text-slate-100",
      style: {
        borderRadius: "50%",
        fontSize: "45px",
        height: "1em",
        textIndent: "-9999em",
        transform: "translateZ(0)",
        width: "1em"
      }
    }
  ) });
};

// src/components/SpinnerIcon/SpinnerIcon.tsx
import "react";
import { jsx as jsx171 } from "react/jsx-runtime";
var SpinnerIcon = ({ className, ...props }) => /* @__PURE__ */ jsx171(
  "svg",
  {
    className: cn("animate-spin", className),
    fill: "none",
    height: "24",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "2",
    viewBox: "0 0 24 24",
    width: "24",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx171("path", { d: "M21 12a9 9 0 1 1-6.219-8.56" })
  }
);

// src/components/StatisticCard/StatisticCard.tsx
import { useEffect as useEffect10 } from "react";
import { motion as motion6, useSpring, useTransform } from "framer-motion";
import { jsx as jsx172, jsxs as jsxs57 } from "react/jsx-runtime";
var StatisticCard = ({ className, icon, label, value, ...props }) => {
  const spring = useSpring(0, { bounce: 0 });
  const rounded = useTransform(spring, (latest) => Math.floor(latest));
  useEffect10(() => {
    spring.set(value);
  }, [spring, value]);
  return /* @__PURE__ */ jsxs57(Card, { className: cn("flex w-full rounded-lg p-4", className), ...props, children: [
    icon && /* @__PURE__ */ jsx172("div", { className: "mr-2 flex items-center justify-center text-4xl", children: icon }),
    /* @__PURE__ */ jsxs57("div", { className: "w-full text-center", children: [
      /* @__PURE__ */ jsx172(motion6.h3, { className: "title-font text-2xl font-semibold text-slate-900 dark:text-slate-100 sm:text-3xl", children: rounded }),
      /* @__PURE__ */ jsx172("p", { className: "font-medium leading-relaxed", children: label })
    ] })
  ] });
};

// src/components/Switch/Switch.tsx
import { forwardRef as forwardRef98 } from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { jsx as jsx173 } from "react/jsx-runtime";
var Switch = forwardRef98(function Switch2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx173(
    SwitchPrimitives.Root,
    {
      className: cn(
        "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
        className
      ),
      ...props,
      ref,
      children: /* @__PURE__ */ jsx173(
        SwitchPrimitives.Thumb,
        {
          className: cn(
            "pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
          )
        }
      )
    }
  );
});

// src/components/Tabs/TabsContent.tsx
import { forwardRef as forwardRef99 } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { jsx as jsx174 } from "react/jsx-runtime";
var TabsContent = forwardRef99(function TabsContent2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx174(
    TabsPrimitive.Content,
    {
      className: cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className
      ),
      ref,
      ...props
    }
  );
});

// src/components/Tabs/TabsList.tsx
import { forwardRef as forwardRef100 } from "react";
import * as TabsPrimitive2 from "@radix-ui/react-tabs";
import { jsx as jsx175 } from "react/jsx-runtime";
var TabsList = forwardRef100(function TabsList2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx175(
    TabsPrimitive2.List,
    {
      className: cn(
        "inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground",
        className
      ),
      ref,
      ...props
    }
  );
});

// src/components/Tabs/TabsRoot.tsx
import { forwardRef as forwardRef101 } from "react";
import { Root as Root20 } from "@radix-ui/react-tabs";
import { jsx as jsx176 } from "react/jsx-runtime";
var TabsRoot = forwardRef101(
  function TabsRoot2(props, ref) {
    return /* @__PURE__ */ jsx176(Root20, { ref, ...props });
  }
);

// src/components/Tabs/TabsTrigger.tsx
import { forwardRef as forwardRef102 } from "react";
import * as TabsPrimitive3 from "@radix-ui/react-tabs";
import { jsx as jsx177 } from "react/jsx-runtime";
var TabsTrigger = forwardRef102(function TabsTrigger2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx177(
    TabsPrimitive3.Trigger,
    {
      className: cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
        className
      ),
      ref,
      ...props
    }
  );
});

// src/components/Tabs/Tabs.tsx
var Tabs = Object.assign(TabsRoot, {
  Content: TabsContent,
  List: TabsList,
  Trigger: TabsTrigger
});

// src/components/ThemeToggle/ThemeToggle.tsx
import { MoonIcon, SunIcon } from "lucide-react";
import { jsx as jsx178, jsxs as jsxs58 } from "react/jsx-runtime";
var ThemeToggle = ({ onClick, variant = "outline", ...props }) => {
  const [theme, setTheme] = useTheme();
  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };
  return /* @__PURE__ */ jsxs58(
    Button,
    {
      size: "icon",
      variant,
      onClick: (event) => {
        toggleTheme();
        onClick?.(event);
      },
      ...props,
      children: [
        /* @__PURE__ */ jsx178(SunIcon, { className: "rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" }),
        /* @__PURE__ */ jsx178(MoonIcon, { className: "absolute rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" })
      ]
    }
  );
};

// src/components/Tooltip/TooltipContent.tsx
import * as React54 from "react";
import { Content as Content13 } from "@radix-ui/react-tooltip";
import { jsx as jsx179 } from "react/jsx-runtime";
var TooltipContent = React54.forwardRef(
  function TooltipContent2({ className, collisionPadding = 0, sideOffset = 4, ...props }, ref) {
    return /* @__PURE__ */ jsx179(
      Content13,
      {
        className: cn(
          "z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        ),
        collisionPadding,
        ref,
        sideOffset,
        ...props
      }
    );
  }
);

// src/components/Tooltip/TooltipRoot.tsx
import "react";
import { Provider, Root as Root21 } from "@radix-ui/react-tooltip";
import { jsx as jsx180 } from "react/jsx-runtime";
var TooltipRoot = ({
  children,
  delayDuration = 0,
  onOpenChange,
  open,
  skipDelayDuration = 300
}) => {
  return /* @__PURE__ */ jsx180(Provider, { delayDuration, skipDelayDuration, children: /* @__PURE__ */ jsx180(Root21, { open, onOpenChange, children }) });
};

// src/components/Tooltip/TooltipTrigger.tsx
import { forwardRef as forwardRef104 } from "react";
import { Trigger as Trigger12 } from "@radix-ui/react-tooltip";
import { jsx as jsx181 } from "react/jsx-runtime";
var TooltipTrigger = forwardRef104(function TooltipTrigger2({ variant = "outline", ...props }, ref) {
  return /* @__PURE__ */ jsx181(Trigger12, { asChild: true, ref, children: /* @__PURE__ */ jsx181(Button, { variant, ...props }) });
});

// src/components/Tooltip/Tooltip.tsx
var Tooltip2 = Object.assign(TooltipRoot, {
  Content: TooltipContent,
  Trigger: TooltipTrigger
});
export {
  Accordion,
  ActionDropdown,
  AlertDialog,
  ArrowToggle,
  Avatar,
  BUTTON_ICON_SIZE,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  ClientTable,
  Collapsible,
  Command2 as Command,
  ContextMenu,
  CopyButton,
  DatePicker,
  Dialog,
  Drawer,
  DropdownButton,
  DropdownMenu,
  ErrorBoundary,
  ErrorFallback,
  FileDropzone,
  Form,
  Heading,
  HoverCard,
  Input,
  Label3 as Label,
  LanguageToggle,
  LineGraph,
  ListboxDropdown,
  MenuBar,
  NotificationHub,
  Pagination,
  Popover,
  Progress,
  RadioGroup4 as RadioGroup,
  Resizable,
  ScrollArea,
  SearchBar,
  Select,
  Separator5 as Separator,
  Sheet,
  Slider,
  Spinner,
  SpinnerIcon,
  StatisticCard,
  Switch,
  Table,
  Tabs,
  TextArea,
  ThemeToggle,
  Tooltip2 as Tooltip,
  badgeVariants,
  buttonVariants,
  labelVariants
};
//# sourceMappingURL=components.js.map